/**
 * 창밖 · 마을 밖.
 *
 * 이 씬은 「눈보라 치는 겨울 밤」 한 문장으로 설명된다. 그 문장이 세 군데에서
 * 같은 값을 써야 한다:
 *
 *   하늘(돔)  —  거리 안개(대기 원근)  —  내리는 눈
 *
 * 어긋나면 바로 티가 난다. 실제로 처음에는 하늘이 단색 검정이고 눈이 GL 점이라,
 * 멀리 있는 눈송이가 **별처럼** 박혀 보였다. 눈보라 치는 밤에 별이 보일 리가 없다.
 *
 * 그래서 이 파일이 지평선 색 하나를 소유하고 안개도 그 색을 쓴다
 * (`horizonColor` 를 main.ts 가 scene.fog 에 넣는다). 멀어질수록 물체가 하늘과
 * 같은 색으로 녹는다 — 그게 대기 원근이다.
 *
 * 눈은 카메라를 따라다니는 부피 안에서 도는 인스턴스 빌보드다. 고정 상자에
 * 뿌리면 마을 반대편에는 눈이 안 온다.
 * (three.js 강수 스킬의 camera-wrapped snow 를 이 씬 규모에 맞춰 줄인 것)
 *
 * 외부 에셋은 쓰지 않는다 — 이 프로젝트의 원칙.
 */

import * as THREE from 'three';
import { GPU_NODES, onGpu } from './gpu';
import { markDecor } from './lightPool';
import { LightPools } from './decals';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { BUILDINGS, CLEARINGS, OUTDOOR, ROADS, WALL_H } from '../../shared/level';
import { getTerrain } from './terrain';
import { SnowTracks } from './tracks';

/** 지평선 색. 하늘 돔의 아래쪽이자 거리 안개의 색이다. 둘은 같아야 한다 */
// 값이 sRGB 로 어두워 보여도 최종 패스(노출 0.82 + ACES + 비네트)를 지나면
// 훨씬 더 어두워진다. 화면에서 '눈보라 치는 밤하늘'로 읽히려면 여기서는
// 생각보다 밝게 잡아야 한다. 눈 오는 밤은 실제로도 새까맣지 않다.
export const horizonColor = new THREE.Color('#6d8299');
const zenithColor = new THREE.Color('#26323f');

export interface Outside {
  update: (t: number, cameraPos: THREE.Vector3) => void;
  /** 눈발 밀도 0..1 */
  setSnow: (fraction: number) => void;
  /**
   * 걷는 사람의 **발** 위치. 눈밭이면 보폭마다 자국이 남는다.
   *
   * 나와 원격 아바타가 같은 문으로 들어온다 — `id` 로만 구분한다.
   */
  footstep: (id: string, x: number, y: number, z: number, yaw: number, indoor: boolean) => void;
  /** 편집 가능한 나무(`OUTDOOR.trees`)만 다시 심는다 — 배치 에디터가 한 그루 옮겼을 때 */
  rebuildTrees: () => void;
}

/** 낮게 깔린 눈구름. 별은 없다 */
function buildSky(): THREE.Mesh {
  // WebGPU 는 ShaderMaterial 을 못 그린다 — 같은 것을 노드로(gpuNodes.ts)
  const mat = GPU_NODES ? GPU_NODES.skyMaterial(zenithColor, horizonColor) : new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
    uniforms: {
      uZenith: { value: zenithColor },
      uHorizon: { value: horizonColor },
    },
    vertexShader: /* glsl */ `
      varying vec3 vDir;
      void main() {
        vDir = normalize(position);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 uZenith;
      uniform vec3 uHorizon;
      varying vec3 vDir;
      void main() {
        // 흐린 밤은 위가 어둡고 지평선이 밝다. 쌓인 눈이 빛을 되쏘기 때문이다
        float h = clamp(vDir.y, -1.0, 1.0);
        float t = pow(clamp(h * 1.15 + 0.1, 0.0, 1.0), 0.75);
        vec3 c = mix(uHorizon, uZenith, t);
        c += uHorizon * 0.35 * smoothstep(0.22, 0.0, abs(h - 0.03));
        gl_FragColor = vec4(c, 1.0);
      }
    `,
  });
  const sky = new THREE.Mesh(new THREE.SphereGeometry(1, 32, 20), mat);
  // 카메라 far(260) 안쪽이어야 한다. 밖에 두면 클립돼서 하늘이 통째로 안 그려진다
  sky.scale.setScalar(180);
  sky.frustumCulled = false;
  sky.renderOrder = -1;
  return sky;
}

/**
 * 카메라를 따라 도는 눈. 인스턴스 빌보드 사각형이고 부피 안에서 wrap 된다.
 * 점(gl_PointSize)으로 그리면 멀리서 별이 된다 — 반드시 면이어야 한다.
 */
function buildSnow(sharedTime: { value: number }) {
  const COUNT = 9000;
  const geo = new THREE.InstancedBufferGeometry();
  // **position 은 전부 0 이다.** 눈송이의 네 귀퉁이는 aCorner 로 따로 넣고, 빌보드는
  // 우리 버텍스 셰이더가 만든다.
  //
  // 왜 이렇게까지 하냐면 — GTAO 같은 프리패스는 재질을 깊이 재질로 바꿔 씬을 다시
  // 그린다. 그때 우리 셰이더가 통째로 무시되고 **원본 사각형(1x1)이 월드 원점에
  // 한 장 그대로 찍힌다.** 마을 마당 한복판에 떠 있던 그 흰 판때기가 이것이다.
  // (조준용 히트박스가 흰 판으로 찍혔던 것과 같은 계열의 버그다.)
  // 꼭짓점을 전부 원점에 모아 두면 어느 패스가 우리 셰이더를 무시하든 넓이가 0 이라
  // 아무것도 안 남는다.
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(12), 3));
  geo.setAttribute(
    'aCorner',
    new THREE.BufferAttribute(new Float32Array([-0.5, -0.5, 0.5, -0.5, 0.5, 0.5, -0.5, 0.5]), 2),
  );
  geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]), 2));
  geo.setIndex([0, 1, 2, 0, 2, 3]);

  // 결정적 난수. 같은 판을 다시 열어도 같은 눈이 온다
  let state = 20261219;
  const rnd = () => ((state = (state * 1664525 + 1013904223) >>> 0) / 4294967296);
  const seed = new Float32Array(COUNT * 3);
  const rand = new Float32Array(COUNT);
  for (let i = 0; i < COUNT; i++) {
    seed[i * 3] = rnd();
    seed[i * 3 + 1] = rnd();
    seed[i * 3 + 2] = rnd();
    rand[i] = rnd();
  }
  geo.setAttribute('aSeed', new THREE.InstancedBufferAttribute(seed, 3));
  geo.setAttribute('aRand', new THREE.InstancedBufferAttribute(rand, 1));

  // **지붕 마스크.** 눈은 카메라를 따라다니는 부피라서, 실내에 들어가면 통째로
  // 꺼서 막았다. 그런데 그건 **밖에서 창문으로 들여다볼 때**는 아무 소용이 없다 —
  // 방 안에 눈이 내리는 게 그대로 보였고, 반대로 안에서 창밖을 보면 마을에
  // 눈이 딱 그쳐 있었다. 부피를 끄는 대신 **건물 아래를 파낸다.**
  // xz 범위 안이고 지붕마루보다 낮은 눈송이는 넓이 0 으로 접는다.
  const MAX_ROOF = 12;
  const roofBox = new Float32Array(MAX_ROOF * 4);
  const roofTop = new Float32Array(MAX_ROOF);

  const uniforms = {
    uTime: sharedTime,
    uWind: { value: new THREE.Vector3(2.1, 0, 0.9) },
    uCameraPos: { value: new THREE.Vector3() },
    uVolume: { value: new THREE.Vector3(70, 34, 70) },
    uRoofBox: { value: roofBox },
    uRoofTop: { value: roofTop },
    uRoofN: { value: 0 },
    uSpeed: { value: 2.6 },
    uSize: { value: 0.05 },
    uSway: { value: 0.75 },
    uOpacity: { value: 0.82 },
    uColor: { value: new THREE.Color('#e8eef6') },
  };

  const mat = new THREE.ShaderMaterial({
    uniforms,
    transparent: true,
    depthWrite: false,
    fog: false,
    vertexShader: /* glsl */ `
      uniform float uTime;
      uniform vec3 uWind;
      uniform vec3 uCameraPos;
      uniform vec3 uVolume;
      uniform float uSpeed;
      uniform float uSize;
      uniform float uSway;
      uniform vec4 uRoofBox[12];
      uniform float uRoofTop[12];
      uniform int uRoofN;
      attribute vec3 aSeed;
      attribute float aRand;
      attribute vec2 aCorner;
      varying vec2 vUv;
      varying float vRand;
      varying float vDist;
      void main() {
        vUv = uv;
        vRand = aRand;
        vec3 vol = uVolume;
        vec3 origin = uCameraPos - vec3(vol.x * 0.5, vol.y * 0.35, vol.z * 0.5);
        float speed = uSpeed * (0.6 + 0.8 * aRand);
        float phase = aRand * 6.2831853;
        vec3 sway = vec3(
          sin(uTime * 0.8 + phase) + 0.4 * sin(uTime * 1.9 + phase * 1.7),
          0.0,
          cos(uTime * 0.7 + phase) + 0.4 * cos(uTime * 1.5 + phase * 1.3)
        ) * uSway * (0.4 + 0.6 * aRand);
        vec3 disp = vec3(uWind.x, -speed, uWind.z) * uTime + sway;
        vec3 p = mod(aSeed * vol + disp - origin, vol) + origin;

        // 지붕 밑이면 접는다. 이 판정이 실내·창 너머 양쪽을 한 번에 끝낸다
        float roofed = 0.0;
        for (int i = 0; i < 12; i++) {
          if (i >= uRoofN) break;
          vec4 r = uRoofBox[i];
          if (p.x > r.x && p.x < r.z && p.z > r.y && p.z < r.w && p.y < uRoofTop[i]) roofed = 1.0;
        }

        // 세로로 늘여서 '흩날리는 것'으로 읽히게 한다
        float size = uSize * (0.55 + 1.1 * aRand) * (1.0 - roofed);
        vec3 right = vec3(viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0]);
        vec3 up = vec3(viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1]);
        vec3 world = p + right * (aCorner.x * size) + up * (aCorner.y * size * 1.9);
        vec4 mv = viewMatrix * vec4(world, 1.0);
        vDist = -mv.z;
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform float uOpacity;
      uniform vec3 uColor;
      varying vec2 vUv;
      varying float vRand;
      varying float vDist;
      void main() {
        vec2 d = vUv - 0.5;
        float disc = smoothstep(0.5, 0.08, length(d * vec2(1.6, 0.7)));
        // 멀리 있는 눈은 뭉개져서 커튼이 된다. 또렷하면 별이 된다
        float far = 1.0 - smoothstep(12.0, 46.0, vDist);
        // 코앞의 눈송이가 화면을 가리면 그건 눈이 아니라 흰 알약이다
        float near = smoothstep(0.5, 2.6, vDist);
        float a = disc * uOpacity * (0.4 + 0.6 * vRand) * mix(0.3, 1.0, far) * near;
        if (a < 0.004) discard;
        gl_FragColor = vec4(uColor, a);
      }
    `,
  });

  // WebGPU 는 ShaderMaterial 을 못 그린다 — 같은 것을 노드로(gpuNodes.ts)
  const mesh = new THREE.Mesh(geo, GPU_NODES ? GPU_NODES.snowMaterial(uniforms, MAX_ROOF) : mat);
  mesh.frustumCulled = false;
  geo.instanceCount = COUNT;
  return { mesh, uniforms, geo, COUNT };
}

/**
 * 바깥.
 *
 * **하늘과 눈 입자만 사건에 공통이다.** 나머지 — 눈 덮인 바닥, 먼 능선,
 * 눈 얹은 나무, 마을 가로등 — 는 전부 「사흘」의 강원 산간 마을이다.
 * 다른 사건에서 그대로 지으면 남의 동네가 배경에 선다: 초치성 점집 담장
 * 너머로 폐교 마을이 보이는 사고가 실제로 났다.
 *
 * @param village 마을 지형을 함께 지을 것인가
 */
/**
 * 눈밭 텍스처 — **통짜 흰 판을 눈으로 만드는 유일한 수단.**
 *
 * 색만 칠한 700m 평면은 밤에 「연보라색 종이」로 보인다. 실제로 그렇게 나왔다.
 * 눈은 (1) 바람이 밀어 놓은 큰 결과 (2) 그 위의 잔 알갱이, 둘로 읽힌다 —
 * 그래서 옥타브를 둘 겹친다. 밝기 진폭은 좁게(0.82~1.0) 잡는다. 넓게 잡으면
 * 눈이 아니라 얼룩진 콘크리트가 된다.
 *
 * 같은 그림을 `bumpMap` 으로도 쓴다. 가로등 빛이 이 요철에 걸려야 **빛 웅덩이의
 * 가장자리가 지저분해지고**, 그 지저분함이 곧 「쌓인 눈」이다. 매끈한 판 위의
 * 원형 그라데이션은 아무리 밝아도 눈으로 안 보인다.
 */
function snowTexture(): THREE.CanvasTexture {
  const N = 256;
  const c = document.createElement('canvas');
  c.width = c.height = N;
  const g = c.getContext('2d')!;
  const img = g.createImageData(N, N);

  // 씨앗 고정 해시. Math.random 을 쓰면 새로고침마다 눈밭이 달라진다
  const h = (x: number, y: number) => {
    const n = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
    return n - Math.floor(n);
  };
  /** 격자 값 노이즈 한 옥타브. 이음매가 없도록 격자 좌표를 period 로 감는다 */
  const octave = (u: number, v: number, period: number) => {
    const x = u * period, y = v * period;
    const xi = Math.floor(x), yi = Math.floor(y);
    const xf = x - xi, yf = y - yi;
    const sx = xf * xf * (3 - 2 * xf), sy = yf * yf * (3 - 2 * yf);
    const w = (i: number, j: number) => h((xi + i) % period, (yi + j) % period);
    const a = w(0, 0) + (w(1, 0) - w(0, 0)) * sx;
    const b = w(0, 1) + (w(1, 1) - w(0, 1)) * sx;
    return a + (b - a) * sy;
  };

  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      const u = x / N, v = y / N;
      // 큰 결(바람 자국) 8칸 + 잔 알갱이 32칸
      const n = octave(u, v, 8) * 0.66 + octave(u, v, 32) * 0.34;
      const val = Math.round(255 * (0.82 + n * 0.18));
      const i = (y * N + x) * 4;
      img.data[i] = img.data[i + 1] = img.data[i + 2] = val;
      img.data[i + 3] = 255;
    }
  }
  g.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
}

/**
 * **위를 향한 면에만 눈이 얹힌다.** 규칙 하나로 축대 비탈·먼 능선·오르막판을
 * 동시에 고친다.
 *
 * 지금까지 이 셋은 「색만 다른 판때기」였다 — 비탈은 연회색 판, 능선은 새까만
 * 삼각형. 눈밭 한가운데인데 눈이 **얹힌 자국**이 어디에도 없으니 판으로 읽혔다.
 * 여기에 눈 텍스처를 한 장 더 붙이는 것으로는 안 된다. 텍스처는 면이 서 있든
 * 누워 있든 똑같이 붙어서, 수직 벽에 눈이 발린 것처럼 된다.
 *
 * 그래서 판정을 **면의 방향**에 맡긴다. 월드 법선의 y 성분 하나가 「이 면에
 * 눈이 앉을 수 있는가」를 결정하고, 월드 XZ 노이즈가 「거기 실제로 남았는가」를
 * 결정한다. 두 값의 곱이 눈의 양이고, 알베도와 거칠기가 **같은 값**을 쓴다.
 *
 * 이 규칙이 각 면에 실제로 주는 값 (직접 계산해서 확인했다):
 * - 축대 비탈(batter 5, 높이 3.6 → 35.8°): N.y 0.585 → 0.36. 흙이 드문드문
 *   드러나는 얼룩진 눈. 레퍼런스 03 의 눈둑이 딱 이렇다
 * - 오르막판(7.5% 경사): N.y 0.997 → 1.0. 통째로 눈
 * - 능선 원뿔(옆면 49.6°): N.y 0.648 → 0.46. 검은 삼각형이 아니라 설산
 * - 축대·건물의 수직면: N.y 0 → 0. 눈 안 붙는다
 *
 * `uSnowFlat` 밑으로는 0 이므로 벽에는 절대 안 붙는다 — precipitation 스킬이
 * 첫 번째 실패 조건으로 꼽은 「수직면에 눈이 붙는다」가 이 한 줄로 막힌다.
 */
export function applySnowAccum(
  mat: THREE.MeshStandardMaterial,
  opts: { flat?: number; coverage?: number; edge?: number; scale?: number; color?: string; bump?: number } = {},
) {
  const u = {
    uSnowFlat: { value: opts.flat ?? 0.35 },
    uSnowCov: { value: opts.coverage ?? 0.7 },
    uSnowEdge: { value: opts.edge ?? 0.15 },
    uSnowScale: { value: opts.scale ?? 0.09 },
    uSnowColor: { value: new THREE.Color(opts.color ?? '#c9d6e4') },
    uSnowBump: { value: opts.bump ?? 0.2 },
  };
  mat.onBeforeCompile = (sh) => {
    Object.assign(sh.uniforms, u);
    // `objectNormal` 은 `beginnormal_vertex` 에서 선언되고 그건 `begin_vertex`
    // **앞**이다. 그래서 여기서 쓸 수 있다. 순서를 뒤집으면 컴파일이 깨진다
    sh.vertexShader = 'varying vec3 vSnowW;\nvarying vec3 vSnowN;\n' + sh.vertexShader.replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>
      vSnowW = (modelMatrix * vec4(transformed, 1.0)).xyz;
      vSnowN = normalize(mat3(modelMatrix) * objectNormal);`,
    );
    sh.fragmentShader = `
      uniform float uSnowFlat;
      uniform float uSnowCov;
      uniform float uSnowEdge;
      uniform float uSnowScale;
      uniform float uSnowBump;
      uniform vec3 uSnowColor;
      varying vec3 vSnowW;
      varying vec3 vSnowN;
      float snowHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float snowNoise(vec2 p) {
        vec2 i = floor(p), f = fract(p);
        vec2 u2 = f * f * (3.0 - 2.0 * f);
        return mix(mix(snowHash(i), snowHash(i + vec2(1.0, 0.0)), u2.x),
                   mix(snowHash(i + vec2(0.0, 1.0)), snowHash(i + vec2(1.0, 1.0)), u2.x), u2.y);
      }
      float snowCover(vec2 p) {
        float n = snowNoise(p) * 0.62 + snowNoise(p * 2.7) * 0.38;
        float t = 1.0 - uSnowCov;
        return smoothstep(t - uSnowEdge, t + uSnowEdge, n);
      }
      // **눈의 높이와 법선이 같은 필드에서 나와야 한다.** precipitation 스킬이
      // 두 번째 실패 조건으로 꼽은 것이 이거다 — 색만 바꾸면 눈둑이 「매끈한
      // 베개」로 읽힌다. 실제로 그랬다. 정점을 밀 필요는 없다: 같은 필드의
      // 기울기를 프래그먼트에서 법선에 더하면 표면에 바람 자국이 생긴다.
      float snowH(vec2 p) { return snowCover(p) * (0.62 + 0.38 * snowNoise(p * 3.1)); }
    ` + sh.fragmentShader
      // 높이(알베도)와 거칠기가 **같은 필드**에서 나와야 한다. 따로 굴리면
      // 「실루엣은 눈인데 반사는 흙」이 되어 판때기로 되돌아간다
      .replace(
        '#include <map_fragment>',
        `#include <map_fragment>
      float sAmt = smoothstep(uSnowFlat, 1.0, clamp(vSnowN.y, 0.0, 1.0))
                 * snowCover(vSnowW.xz * uSnowScale);
      diffuseColor.rgb = mix(diffuseColor.rgb, uSnowColor, sAmt);`,
      )
      .replace(
        '#include <roughnessmap_fragment>',
        `#include <roughnessmap_fragment>
      roughnessFactor = mix(roughnessFactor, 0.82, sAmt);`,
      )
      .replace(
        '#include <normal_fragment_maps>',
        `#include <normal_fragment_maps>
      {
        vec2 sP = vSnowW.xz * uSnowScale;
        float sE = 0.4;
        float sH0 = snowH(sP);
        vec2 sG = vec2(snowH(sP + vec2(sE, 0.0)) - sH0, snowH(sP + vec2(0.0, sE)) - sH0) / sE;
        normal = normalize(normal + mat3(viewMatrix) * vec3(-sG.x, 0.0, -sG.y) * uSnowBump * sAmt);
      }`,
      );
  };
  mat.customProgramCacheKey = () => 'snowAccum';
  onGpu(mat, (n, m) => n.snowAccum(m, u));   // WebGPU 판(gpuNodes.ts)
  return mat;
}

export function buildOutside(scene: THREE.Scene, village = true): Outside {
  const g = new THREE.Group();
  scene.add(g);
  const time = { value: 0 };

  const sky = buildSky();
  g.add(sky);

  /** 마을 지형은 「사흘」의 것이다. 다른 사건에서는 통째로 안 짓는다 */
  const vg = new THREE.Group();
  if (village) g.add(vg);

  // 눈 덮인 마을 바닥. 건물 바닥(y=0) 밑에 깔아서 실내에서는 안 보인다.
  //
  // 눈은 반사율이 높다 — 알베도를 너무 어둡게 잡으면 밤에 아무것도 안 보인다.
  // 그렇다고 #c6d3e2 로 두면 하늘빛만으로 화면이 꽉 차서 **가로등이 켜져 있는지
  // 아닌지가 안 보인다**(실제로 그랬다: 등 아래에 빛 웅덩이가 없었다).
  // 한 단 낮춰서 등 없는 눈밭은 푸르게 가라앉히고, 밝은 자리를 등이 만들게 한다.
  const snowTex = snowTexture();
  const groundTex = snowTex.clone();
  groundTex.needsUpdate = true;
  groundTex.repeat.set(175, 175); // 700m / 175 = 4m 마다 한 장
  const groundMat = new THREE.MeshStandardMaterial({
    color: '#a6b6cc', roughness: 0.94,
    map: groundTex, bumpMap: groundTex, bumpScale: 0.6,
  });
  // 평지 700×700 은 먼 배경으로 남기고, 마을 범위는 **높이맵 지형 메시**가 덮는다(있을 때)
  const ground = new THREE.Mesh(new THREE.PlaneGeometry(700, 700), groundMat);
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = -0.03;
  ground.receiveShadow = true;
  vg.add(ground);
  const terrain = getTerrain();
  // 재질을 복제한다 — 에디터의 높이 색(vertexColors)이 배경 평면까지 물들이면 안 된다
  if (terrain) { const tm = terrain.buildMesh(groundMat.clone(), -0.02); tm.receiveShadow = true; vg.add(tm); }

  // 물건 위에 얹히는 눈은 **바닥보다 밝아야** 한다. 같은 색이면 조회대도 담장도
  // 눈밭에 녹아 사라진다. 결은 같은 텍스처를 잘게 반복해서 준다
  const capTex = snowTex.clone();
  capTex.needsUpdate = true;
  capTex.repeat.set(2, 2);
  const snowMat2 = new THREE.MeshStandardMaterial({
    color: '#c9d6e4', roughness: 0.96, map: capTex, bumpMap: capTex, bumpScale: 0.3,
  });
  snowMat2.name = 'snow-cap';
  const place = (geo: THREE.BufferGeometry, mat: THREE.Material, x: number, y: number, z: number) => {
    const m = new THREE.Mesh(geo, mat);
    m.position.set(x, y, z);
    m.castShadow = true;
    return m;
  };

  // 먼 능선 — 안개에 반쯤 지워진 실루엣이면 충분하다
  // 눈 덮인 산이다. 검게만 두면 밤하늘에 붙은 검은 삼각형 스물둘이 된다
  /**
   * **먼 능선.**
   *
   * 원뿔 22개 → 실루엣 띠 3겹 → 지금이 세 번째다. 앞의 둘이 왜 안 됐는지가
   * 이 코드의 이유다: 원뿔은 정삼각형이라 산이 아니었고, 띠는 값만 갈리는 **벽**
   * 이었다. 실제 능선을 보면 다섯 가지가 동시에 있다.
   *
   *  (ㄱ) 선이 하나가 아니다 — 지릉이 보는 쪽으로 뻗어 내려와 V 자 골을 만들고
   *       봉우리는 좌우가 비대칭이며 사이에 안부가 있다.
   *  (ㄴ) 멀수록 하늘에 녹는다 — 지평선 하늘(`horizonColor` #6d8299)이 천정보다 밝다.
   *  (ㄷ) 골이 봉우리보다 밝다 — 눈 오는 밤엔 골에 안개가 고인다. 값이 위→아래로
   *       어두움→밝음이다. 앞의 두 판은 정반대였다.
   *  (ㄹ) 눈은 마루와 달 쪽 면에만, 그 아래는 침엽수림이라 어둡고 잘게 얼룩진다.
   *       설선은 자로 그은 선이 아니다.
   *  (ㅁ) 달 쪽 면이 밝다 — 방향이 없는 색은 벽이 된다. emissive 판이 그랬다.
   *
   * 그래서 실루엣 폴리라인이 아니라 **높이장 띠**를 짠다. 둘레 N × 깊이 M 격자로,
   * 마루는 뒤(v=0)에 있고 지릉이 앞(v=1)으로 흘러내린다. 마루선은 ridged 노이즈
   * (1−|2n−1|) 라 날이 서고, 각도를 다른 노이즈로 밀어(도메인 워프) 봉우리 좌우가
   * 비대칭이 된다. 면은 non-indexed 라 평면 셰이딩 — 각진 면이 멀리서 바위 결이다.
   *
   * 색은 씬 조명에 맡기지 않는다. 안개를 맡겼더니 150m 에서 97% 라 세 겹이 한 색으로
   * 뭉갰고(앞뒤 차이 3), 조명에 맡겼더니 하늘보다 어두운 실루엣이 됐다(차이 6).
   * 달 방향 램버트·눈·숲·골안개·공기원근을 **셰이더에서 직접** 계산한다.
   */
  {
    const MOON = new THREE.Vector3(-24, 26, -20).normalize();
    const BOT = -6;
    const hash1 = (n: number) => {
      const v = Math.sin(n * 127.1 + 311.7) * 43758.5453;
      return v - Math.floor(v);
    };
    const sm = (t: number) => t * t * (3 - 2 * t);
    const vnoise = (x: number, seed: number) => {
      const i0 = Math.floor(x), f = sm(x - i0);
      return hash1(i0 + seed) * (1 - f) + hash1(i0 + 1 + seed) * f;
    };
    /** 날 선 마루 — 골은 둥글고 마루는 뾰족해진다 */
    const ridged = (x: number, seed: number) => 1 - Math.abs(2 * vnoise(x, seed) - 1);

    const RINGS = [
      { r: 132, depth: 34, base: 15, amp: 21, seed: 11, haze: 0.20, snow: 0.62 },
      { r: 182, depth: 44, base: 25, amp: 29, seed: 37, haze: 0.55, snow: 0.54 },
      { r: 242, depth: 54, base: 37, amp: 37, seed: 73, haze: 0.84, snow: 0.46 },
    ];
    const N = 132, M = 6;

    for (const rg of RINGS) {
      const grid: [number, number, number][][] = [];
      let peak = 0;
      for (let k = 0; k <= N; k++) {
        const a = (k / N) * Math.PI * 2;
        // 각도를 둘레 길이(m)로 바꿔 파장을 미터로 준다 — 반지름이 달라도 봉우리 폭이 같다
        const u = a * rg.r;
        // **도메인 워프.** 마루를 한쪽으로 밀면 한 면은 급하고 반대는 완만해진다
        const uw = u + 34 * (vnoise(u / 190, rg.seed + 2) - 0.5);
        const crest =
          rg.base +
          rg.amp * (0.60 * ridged(uw / 125, rg.seed) +
                    0.27 * ridged(uw / 46, rg.seed + 5) +
                    0.13 * vnoise(uw / 15, rg.seed + 9));
        // 지릉이 길게 뻗는 곳과 짧은 곳 — 이게 V 자 골의 간격을 만든다
        const spur = 0.4 + 0.6 * vnoise(u / 88, rg.seed + 4);
        const col: [number, number, number][] = [];
        for (let m = 0; m <= M; m++) {
          const v = m / M;
          const p = 1.15 + 1.9 * (1 - spur);
          let y = BOT + (crest - BOT) * Math.pow(1 - v, p);
          // 사면 잔결
          y += rg.amp * 0.05 * (vnoise(u / 9 + v * 4, rg.seed + 13) - 0.5);
          const rr = (rg.r - v * rg.depth) * (1 + 0.03 * (vnoise(u / 70, rg.seed + 3) - 0.5));
          col.push([Math.cos(a) * rr, y, Math.sin(a) * rr]);
          if (y > peak) peak = y;
        }
        grid.push(col);
      }

      const pos: number[] = [];
      const push = (q: [number, number, number]) => pos.push(q[0], q[1], q[2]);
      for (let k = 0; k < N; k++) {
        for (let m = 0; m < M; m++) {
          const A = grid[k][m], B = grid[k + 1][m], C = grid[k + 1][m + 1], D = grid[k][m + 1];
          push(A); push(C); push(B);
          push(A); push(D); push(C);
        }
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      g.computeVertexNormals();   // non-indexed 라 면마다 법선 = 평면 셰이딩

      const mat = new THREE.ShaderMaterial({
        side: THREE.DoubleSide,
        fog: false,
        uniforms: {
          uMoon: { value: MOON },
          uHorizon: { value: horizonColor },
          uDark: { value: new THREE.Color('#2b3644') },
          uLit: { value: new THREE.Color('#57687f') },
          uForest: { value: new THREE.Color('#243139') },
          uSnow: { value: new THREE.Color('#c4d3e4') },
          uHaze: { value: rg.haze },
          /**
           * **먼 겹은 눈도 옅어야 물러난다.** 겹마다 안개만 올리면 눈이 그대로
           * 하얘서 먼 산이 앞으로 튀어나온다 — 잰 값이 하늘과의 차이 31/28/26 으로
           * 거의 안 갈렸다. 눈 세기를 안개에 반비례시킨다.
           */
          uSnowMul: { value: 1 - rg.haze * 0.55 },
          uSnowY: { value: BOT + (peak - BOT) * rg.snow },
          uSnowW: { value: rg.amp * 0.22 },
          uBot: { value: BOT },
          uTop: { value: peak },
        },
        vertexShader: /* glsl */ `
          varying vec3 vW;
          varying vec3 vN;
          void main() {
            vec4 w = modelMatrix * vec4(position, 1.0);
            vW = w.xyz;
            vN = normalize(mat3(modelMatrix) * normal);
            gl_Position = projectionMatrix * viewMatrix * w;
          }
        `,
        fragmentShader: /* glsl */ `
          uniform vec3 uMoon, uHorizon, uDark, uLit, uForest, uSnow;
          uniform float uHaze, uSnowY, uSnowW, uBot, uTop, uSnowMul;
          varying vec3 vW;
          varying vec3 vN;
          float rh(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
          float rn(vec2 p) {
            vec2 i = floor(p), f = p - i;
            f = f * f * (3.0 - 2.0 * f);
            return mix(mix(rh(i), rh(i + vec2(1.0, 0.0)), f.x),
                       mix(rh(i + vec2(0.0, 1.0)), rh(i + vec2(1.0, 1.0)), f.x), f.y);
          }
          void main() {
            vec3 n = normalize(vN);
            // 랩 램버트 — 밤이라 그림자 쪽도 완전히 죽으면 안 된다
            float ndl = clamp(dot(n, uMoon) * 0.5 + 0.5, 0.0, 1.0);
            vec3 rock = mix(uDark, uLit, pow(ndl, 1.4));
            // 숲 — 잘게 얼룩진다. 이 결이 없으면 아래가 단색 판이다
            float sp = rn(vW.xz * 0.19) * 0.65 + rn(vW.xz * 0.63) * 0.35;
            vec3 forest = uForest * (0.78 + 0.5 * sp) * (0.6 + 0.4 * ndl);
            vec3 base = mix(forest, rock, 0.34);
            // 설선 — 높이로 가르되 노이즈로 흔들고, 급한 면에는 안 붙는다
            float jit = (rn(vW.xz * 0.075) - 0.5) * uSnowW * 2.2;
            float snow = smoothstep(uSnowY - uSnowW, uSnowY + uSnowW, vW.y + jit)
                       * smoothstep(0.28, 0.64, n.y)
                       * (0.5 + 0.5 * ndl);
            vec3 col = mix(base, uSnow * (0.52 + 0.52 * ndl), snow * uSnowMul);
            // 골안개 — 아래로 갈수록 하늘색에 잠긴다. 이것이 골을 마루보다 밝게 만든다
            float valley = 1.0 - smoothstep(uBot, uTop * 0.82, vW.y);
            float haze = clamp(uHaze + valley * 0.42, 0.0, 0.94);
            col = mix(col, uHorizon, haze);
            gl_FragColor = vec4(col, 1.0);
          }
        `,
      });
      const m = new THREE.Mesh(g, GPU_NODES ? GPU_NODES.ridgeMaterial(mat.uniforms) : mat);   // WebGPU 는 노드로(gpuNodes.ts)
      m.frustumCulled = false;
      vg.add(m);
    }
  }

  /* --- 언덕 --- */
  // 단(`slabs` 의 y)만 얹으면 밑이 뚫린다. 실제로 언덕 밑에서 올려다보니
  // 한서진 집 바닥 뒷면과 벽 밑동, 그리고 축대 벽 두 장이 새까맣게 보였다.
  //
  // 꼭대기 사각형과 그것을 `batter` 만큼 바깥으로 벌린 아래 사각형을 잇는
  // 네 면(각뿔대 옆면)이면 모서리에 틈이 안 생긴다.
  // 길이 올라오는 구간(`gapS`)만 z2 쪽 면에서 빼 둔다 — 거기는 비탈판이 덮는다.
  // 비탈에 박을 돌은 `putRock` 이 정의된 뒤에 뿌린다 — 언덕 정보만 모아 둔다
  const moundRocks: [NonNullable<typeof OUTDOOR.mounds>[number], number][] = [];
  for (const m of OUTDOOR.mounds ?? []) {
    const b = m.batter ?? 4;
    // 사면 35.8° → 눈이 0.36 만큼만 남는다. 흙이 드문드문 드러나야 「비탈」이다
    // uv 가 이미 「3m 마다 한 장」이라 repeat 은 1 이다. 도로에서 겪었듯이
    // bumpMap 을 잘게 반복하면 법선이 과하게 흔들려 면이 **검게** 죽는다
    const mTex = snowTex.clone();
    mTex.repeat.set(1, 1);
    mTex.needsUpdate = true;
    const earth = applySnowAccum(
      // 언덕 옆면(batter 8 / 높이 3.6 → 24°)도 경사 항으로는 마루와 구분이
      // 안 된다. 여기도 노이즈 칸을 1m 아래로 줄이고 결을 넣는다 — 흙이
      // 「비탈 한 장」이 아니라 눈 사이로 드러난 자국이어야 한다.
      new THREE.MeshStandardMaterial({
        color: '#615a51', roughness: 0.97,
        map: mTex, bumpMap: mTex, bumpScale: 0.14,
      }),
      { flat: 0.28, coverage: 0.94, edge: 0.16, scale: 0.9, bump: 0.16 },
    );
    earth.name = 'mound-earth';
    const parts: THREE.BufferGeometry[] = [];
    /**
     * 꼭대기 모서리 (ax,az)→(bx,bz) 와 그것을 (ox,oz) 만큼 벌린 밑변을 잇는다.
     *
     * **도는 방향이 곧 바깥면이다.** 처음에 네 면을 전부 반대로 감아서
     * 법선이 언덕 속을 향했고, 조감도에서 언덕이 새까만 판때기 네 장이 됐다.
     * 규칙: 진행 방향 d 에 대해 (dz, -dx) 가 (ox, oz) 와 같은 쪽이어야 한다.
     */
    const frac2 = (n: number) => n - Math.floor(n);
    const hash = (i: number, j: number, s: number) =>
      frac2(Math.sin(i * 127.1 + j * 311.7 + s * 74.7) * 43758.5453);
    /**
     * 꼭대기 모서리 (ax,az)→(bx,bz) 와 그것을 (ox,oz) 만큼 벌린 밑변을 잇는다.
     *
     * **도는 방향이 곧 바깥면이다.** 처음에 네 면을 전부 반대로 감아서
     * 법선이 언덕 속을 향했고, 조감도에서 언덕이 새까만 판때기 네 장이 됐다.
     * 규칙: 진행 방향 d 에 대해 (dz, -dx) 가 (ox, oz) 와 같은 쪽이어야 한다.
     *
     * **사각형 한 장으로는 눈이 쌓일 곳이 없다.** 그게 두 바퀴째 남은 결함이었다 —
     * `applySnowAccum` 은 면의 방향을 보고 눈을 얹는데, 48m 짜리 면 전체가 법선
     * 하나(N.y=0.81)를 공유하니 위아래 없이 똑같은 값이 칠해졌다. 밤에 가로등을
     * 받으면 그게 매끈한 널빤지 두 장으로 보인다. 색을 고쳐도 안 낫는다 —
     * **없는 건 색이 아니라 요철이다.**
     *
     * 그래서 격자로 쪼개고 꼭짓점을 흔든다. 규칙 셋:
     *  - 옆 모서리(u 0/1)는 고정한다. 이웃 면과 벌어지면 언덕에 틈이 뚫린다
     *  - 밑변은 y 를 안 건드린다. 지면을 뚫거나 뜬다
     *  - 꼭대기 모서리는 **바깥쪽으로만** 흔든다. 위아래로 흔들면 평지 슬래브와
     *    어긋나는데, 깊이로 흔들어도 아래에서 올려다보면 능선이 충분히 울렁인다
     */
    const face = (ax: number, az: number, bx: number, bz: number, ox: number, oz: number) => {
      const len = Math.hypot(bx - ax, bz - az);
      const cols = Math.max(2, Math.round(len / 1.7));
      const rows = 5;
      const on = Math.hypot(ox, oz) || 1;
      const ux = ox / on, uz = oz / on;
      const slope = Math.hypot(on, m.y);
      const pos: number[] = [], uv: number[] = [], idx: number[] = [];
      for (let j = 0; j <= rows; j++) {
        const v = j / rows;                       // 0 = 꼭대기, 1 = 밑
        for (let i = 0; i <= cols; i++) {
          const u = i / cols;
          let x = ax + (bx - ax) * u + ox * v;
          let z = az + (bz - az) * u + oz * v;
          let y = m.y * (1 - v);
          if (i > 0 && i < cols) {
            // 바깥으로 밀고 당긴다. 아래로 갈수록 크게 — 산자락이 퍼지는 모양
            const d = (hash(i, j, 1) - 0.5) * 1.5 * (0.4 + v);
            x += ux * d; z += uz * d;
            // 세로 요철은 위·아래 끝에서 0 이라야 슬래브·지면과 안 어긋난다
            y += (hash(i, j, 7) - 0.5) * 0.8 * Math.sin(Math.PI * v);
          }
          pos.push(x, y, z);
          uv.push((u * len) / 3, (v * slope) / 3);
        }
      }
      const w2 = cols + 1;
      for (let j = 0; j < rows; j++)
        for (let i = 0; i < cols; i++) {
          const a = j * w2 + i;
          idx.push(a, a + 1, a + w2 + 1, a, a + w2 + 1, a + w2);
        }
      const g2 = new THREE.BufferGeometry();
      g2.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      g2.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
      g2.setIndex(idx);
      g2.computeVertexNormals();
      parts.push(g2);
    };
    /**
     * 마루에 얹는 눈처마.
     *
     * 비탈면을 아무리 흔들어도 **능선이 자로 그은 직선이면 언덕이 아니다.**
     * 고원이 평평한 슬래브라 그 윗변은 구조적으로 곧다 — 비탈 쪽 요철로는
     * 못 고친다(깊이로만 흔들려서 정면에서는 그대로 직선이다).
     *
     * 그래서 능선 자체를 건드리지 않고 **위에 얹는다.** 바람이 사면 꼭대기에
     * 눈을 쌓아 만드는 처마가 실제로 그렇게 생겼다 — 0.1~0.6m 로 들쭉날쭉하고
     * 고원 쪽으로 흘러내린다. 하늘과 만나는 선이 이걸로 울퉁불퉁해진다.
     */
    const corniceParts: THREE.BufferGeometry[] = [];
    const cornice = (ax: number, az: number, bx: number, bz: number, ox: number, oz: number) => {
      const on = Math.hypot(ox, oz) || 1;
      const ux = ox / on, uz = oz / on;
      const len = Math.hypot(bx - ax, bz - az);
      const n = Math.max(3, Math.round(len / 1.2));
      const pos: number[] = [], uv: number[] = [], idx: number[] = [];
      for (let i = 0; i <= n; i++) {
        const t = i / n;
        const px = ax + (bx - ax) * t, pz = az + (bz - az) * t;
        const j1 = hash(i, 0, 3), j2 = hash(i, 0, 11);
        const wave = 0.5 + 0.5 * Math.sin(t * len * 0.85 + ox * 0.7 + oz * 1.3);
        const h = 0.1 + wave * 0.36 + j1 * 0.18;
        const din = 0.5 + j2 * 0.7, dout = 0.3 + j1 * 0.5;
        // 고원 쪽 발치 · 마루 · 비탈 쪽 발치(사면을 따라 내려간다)
        pos.push(px - ux * din, m.y + 0.02, pz - uz * din);
        pos.push(px + ux * 0.06 * j2, m.y + h, pz + uz * 0.06 * j2);
        pos.push(px + ux * dout, m.y - (dout * m.y) / b, pz + uz * dout);
        for (let r = 0; r < 3; r++) uv.push((t * len) / 3, r / 2);
      }
      for (let i = 0; i < n; i++)
        for (let r = 0; r < 2; r++) {
          const a = i * 3 + r;
          idx.push(a, a + 1, a + 4, a, a + 4, a + 3);
        }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
      g.setIndex(idx);
      g.computeVertexNormals();
      // 네 변이 저마다 다른 방향을 보므로 감는 순서를 하나로 못 정한다.
      // 법선을 내 보고 아래를 보면 뒤집는다 — 위를 향한 띠라는 건 확실하니까
      const nn = g.getAttribute('normal');
      let sum = 0;
      for (let k = 0; k < nn.count; k++) sum += nn.getY(k);
      if (sum < 0) {
        const ix = Array.from(g.getIndex()!.array);
        for (let k = 0; k < ix.length; k += 3) [ix[k + 1], ix[k + 2]] = [ix[k + 2], ix[k + 1]];
        g.setIndex(ix);
        g.computeVertexNormals();
      }
      corniceParts.push(g);
    };

    face(m.x1, m.z1, m.x2, m.z1, 0, -b);                    // z1 쪽 (뒤)
    face(m.x1, m.z2, m.x1, m.z1, -b, 0);                    // x1 쪽 (서)
    face(m.x2, m.z1, m.x2, m.z2, b, 0);                     // x2 쪽 (동)
    if (m.gapS) {
      face(m.gapS[0], m.z2, m.x1, m.z2, 0, b);
      face(m.x2, m.z2, m.gapS[1], m.z2, 0, b);
    } else {
      face(m.x2, m.z2, m.x1, m.z2, 0, b);
    }
    cornice(m.x1, m.z1, m.x2, m.z1, 0, -b);
    cornice(m.x1, m.z2, m.x1, m.z1, -b, 0);
    cornice(m.x2, m.z1, m.x2, m.z2, b, 0);
    if (m.gapS) {
      cornice(m.gapS[0], m.z2, m.x1, m.z2, 0, b);
      cornice(m.x2, m.z2, m.gapS[1], m.z2, 0, b);
    } else {
      cornice(m.x2, m.z2, m.x1, m.z2, 0, b);
    }
    const cm = mergeGeometries(corniceParts, false);
    for (const geo of corniceParts) geo.dispose();
    if (cm) {
      const cMesh = new THREE.Mesh(cm, applySnowAccum(
        new THREE.MeshStandardMaterial({ color: '#b9c4d2', roughness: 0.95 }),
        { flat: 0.4, coverage: 0.95, edge: 0.1, scale: 0.35, color: '#e2ecf8' },
      ));
      cMesh.receiveShadow = true;
      vg.add(cMesh);
    }

    const merged = mergeGeometries(parts, false);
    for (const geo of parts) geo.dispose();
    if (merged) {
      const mesh = new THREE.Mesh(merged, earth);
      mesh.receiveShadow = true;
      vg.add(mesh);
    }
    moundRocks.push([m, b]);
  }

  /* --- 비탈에 드러난 돌 --- */
  //
  // 브리프가 처음부터 요구한 것 — 「급한 사면은 흙·돌이 드러난다」. 다섯 바퀴를
  // 도는 동안 한 번도 못 지켰다. 눈 쌓임 규칙(`applySnowAccum`)만으로는 못 한다:
  // 그건 **면의 기울기**로 눈을 얹는데, 비탈면 전체가 같은 기울기라 통째로
  // 하얗거나 통째로 흙이다. 그래서 화면이 매끈한 이불 한 장이 됐다.
  //
  // 필요한 건 **기울기가 다른 작은 면들**이다. 바위 하나를 얹으면 그 위는 눈이
  // 얹히고 옆구리는 드러난다. 그 대비가 미터 단위로 흩어져야 비탈이 비탈로 읽힌다.
  const rockParts: THREE.BufferGeometry[] = [];
  const putRock = (x: number, y: number, z: number, r: number, sd: number) => {
    const fr = (n: number) => n - Math.floor(n);
    const rnd = (k: number) => fr(Math.sin(sd * 12.9898 + k * 78.233) * 43758.5453);
    const g = new THREE.IcosahedronGeometry(r, 0);
    const pa = g.getAttribute('position');
    // 정이십면체 그대로면 「공」이다. 꼭짓점을 흔들어 각을 만든다
    for (let i = 0; i < pa.count; i++) {
      const k = 0.62 + rnd(i * 3.1) * 0.76;
      pa.setXYZ(i, pa.getX(i) * k, pa.getY(i) * k, pa.getZ(i) * k);
    }
    g.computeVertexNormals();
    // **납작하게 누르면 눈 위에 놓인 판때기가 된다.** y 를 0.62 배까지 눌렀더니
    // 비탈에 검은 딱지가 붙은 그림이 나왔다. 바위는 파묻힌 덩어리다 — 높이를
    // 살리고 대신 더 깊이 묻는다.
    // 가로로 1.4 배까지 늘리면 다시 판때기가 된다. 위아래를 살리고 가로를 죈다
    g.scale(1, 0.95 + rnd(41) * 0.5, 0.82 + rnd(53) * 0.36);
    g.rotateY(rnd(61) * Math.PI * 2);
    g.rotateX((rnd(71) - 0.5) * 0.5);
    // 55% 를 땅에 묻는다. 얹어 두면 굴러다니는 자갈로 보인다
    g.translate(x, y - r * 0.55, z);
    rockParts.push(g);
  };

  // 언덕 비탈. gapS(길이 지나는 구간)는 빼고 남·동·서 세 면에 뿌린다
  for (const [m, b] of moundRocks) {
    const fr2 = (n: number) => n - Math.floor(n);
    const rh = (i: number, k: number) => fr2(Math.sin(i * 157.3 + k * 91.7) * 43758.5453);
    for (let k = 0; k < 120; k++) {
      const out = 0.12 + rh(k, 1) * 0.78;          // 0 = 마루, 1 = 밑동
      const y = m.y * (1 - out);
      const side = k % 3;                           // 0 남, 1 서, 2 동
      let x: number, z: number;
      if (side === 0) {
        const gp = m.gapS;
        const u = rh(k, 2);
        x = gp
          ? (u < 0.5 ? m.x1 + (gp[0] - m.x1) * (u * 2) : gp[1] + (m.x2 - gp[1]) * ((u - 0.5) * 2))
          : m.x1 + (m.x2 - m.x1) * u;
        z = m.z2 + out * b;
      } else if (side === 1) {
        x = m.x1 - out * b;
        z = m.z2 + (m.z1 - m.z2) * rh(k, 3);
      } else {
        x = m.x2 + out * b;
        z = m.z2 + (m.z1 - m.z2) * rh(k, 3);
      }
      // 크기를 한쪽으로 몰지 않는다 — 큰 놈 몇에 잔돌 여럿이라야 비탈에 눈금이 선다.
      // **다만 위를 0.91 까지 열어 뒀던 게 문제였다.** 반지름 0.9 를 55% 묻으면
      // 폭 1.8m 에 높이 0.41m — 비율 0.23 이라 어떤 각도에서도 납작한 판이다.
      // 위를 0.62 로 내려 폭 1.2m / 높이 0.36m(0.29)로 만든다.
      putRock(x, y, z, 0.15 + rh(k, 4) ** 2.4 * 0.47, k + 301);
    }
  }

  /* --- 성토 둑길 --- */
  //
  // 언덕에 뚫은 구멍(`gapS`)을 흙으로 메운다. 판을 하나 더 얹는 게 아니라
  // **단면을 잡고 z 를 따라 밀어낸다** — 각 z 마다 [서쪽 발치, 서쪽 어깨,
  // 동쪽 어깨, 동쪽 발치] 네 점을 만들고 그걸 띠로 잇는다. 어깨 사이가 노면,
  // 어깨~발치가 옆비탈이다. 높이가 0 인 아래쪽 끝에서는 네 점이 한 줄로
  // 모여 지면과 이음매 없이 만난다.
  //
  // 비탈면은 언덕과 **같은 흙 재질**을 쓴다. 다른 재질을 쓰면 둑과 언덕이
  // 만나는 자리에 색 경계가 생겨 「붙여 놓은 것」으로 보인다.
  for (const cwy of OUTDOOR.causeways ?? []) {
    const frac3 = (n: number) => n - Math.floor(n);
    const hash = (i: number, j: number, sd: number) =>
      frac3(Math.sin(i * 127.1 + j * 311.7 + sd * 74.7) * 43758.5453);
    const bt = cwy.batter ?? 2.6;
    const dz = cwy.zHigh - cwy.zLow;
    // **카메라 발밑 2m 에 놓인 사각형은 무슨 재질을 발라도 판지다.** 05 컷
    // 아래쪽을 먹던 크림색 다각형들이 그것이었다 — 폭 2m 짜리 마루 사각형이
    // 화면에서는 1/4 을 차지한다. 칸을 잘게 쪼개는 것 말고는 방법이 없다.
    const rows = Math.max(8, Math.round(Math.abs(dz) / 0.9));
    const cols = 8;                                  // 발치·어깨 사이를 쪼갠다
    const pos: number[] = [], uv: number[] = [], idx: number[] = [];
    //
    // **둑 윗면을 평평하게 두면 길이 사라진다.** 노면과 둑이 같은 높이면 눈이
    // 똑같이 덮여 언덕 전체가 흰 이불 한 장이 됐다. 실제 산길은 둑을 쌓은 뒤
    // 그 위를 **깎아서** 낸다 — 양 어깨가 남고 가운데가 파인다. 그 골이 길이다.
    const SH = 0.44;                                 // 어깨가 노면보다 높은 높이
    const RW = 0.55;                                 // 노면 폭 = 둑 폭의 0.55
    // [x 기준, 바깥으로 벌어지는 정도 0~1, 노면 대비 높이]
    const lane: [number, number, number][] = [];
    for (let i = 0; i <= cols; i++) lane.push([cwy.x1, 1 - i / cols, SH]);  // 서쪽 비탈 → 어깨
    lane.push([cwy.x1 * RW, 0, 0]);                                        // 노면 서쪽 가
    lane.push([cwy.x2 * RW, 0, 0]);                                        // 노면 동쪽 가
    for (let i = 0; i <= cols; i++) lane.push([cwy.x2, i / cols, SH]);     // 어깨 → 동쪽 비탈
    const W = lane.length;
    for (let j = 0; j <= rows; j++) {
      const t = j / rows;
      const z = cwy.zLow + dz * t;
      const h = cwy.yHigh * t;
      for (let i = 0; i < W; i++) {
        const [bx, out, add] = lane[i];
        // out 0 = 노면 가장자리, 1 = 발치(지면). 어깨는 out 0 이면서 add 만큼 높다
        const top = h + add;
        let x = bx + (bx < 0 ? -1 : 1) * out * bt * top;
        let y = top * (1 - out);
        let z2 = z;
        // 흙둑도 자로 그은 쐐기면 판지다. 비탈 한가운데만 흔든다
        if (out > 0.02 && out < 0.98 && j > 0 && j < rows) {
          const d = (hash(i, j, 5) - 0.5) * 0.9;
          x += (bx < 0 ? -1 : 1) * d;
          y += (hash(i, j, 13) - 0.5) * 0.35 * Math.sin(Math.PI * out);
          z2 += (hash(i, j, 23) - 0.5) * 0.5;
        } else if (out <= 0.02 && j > 0 && j < rows) {
          // **마루에는 흔들림이 하나도 없었다.** 그래서 눈둑 가장자리가 자로
          // 그은 직선으로 떨어졌고, 그 직선이 「판때기」의 정체다. 노면 가로는
          // x 를 안 건드린다 — 길 폭이 들쭉날쭉해지면 길이 아니게 된다
          const onRoadEdge = i === cols + 1 || i === cols + 2;
          if (!onRoadEdge) x += (bx < 0 ? -1 : 1) * (hash(i, j, 31) - 0.5) * 0.55;
          y += (hash(i, j, 43) - 0.5) * 0.06;
          z2 += (hash(i, j, 53) - 0.5) * 0.35;
        }
        pos.push(x, y, z2);
        uv.push(x / 3, z2 / 3);
      }
    }
    //
    // **황토색 판때기의 정체가 여기였다.** 「언덕에서 마을 내려다보기」 컷 전경을
    // 가로막던 그 넓은 베이지 사각형들은 언덕 흙도 노면도 아니고 **둑 윗면**이었다
    // (화면에 광선을 쏴서 확인했다: 390 삼각형 = rows 13 × 15 × 2, 이 메쉬다).
    // 어깨에서 노면 가로 내려오는 폭 2m 짜리 사각형이 카메라 발밑 2m 에 있으니
    // 화면의 1/4 을 흙색 한 장이 먹는다.
    //
    // 값을 만져서 될 일이 아니었다 — 둑은 **비탈이 완만해서**(21°) 눈 쌓임 규칙의
    // 경사 항이 마루와 옆구리를 구분하지 못한다. 그래서 요소를 나눈다:
    // 마루(어깨~노면)는 사람이 밟지 않는 **깊은 눈**이고, 바깥 옆비탈만 흙이
    // 드러나는 사면이다. 실제 눈 덮인 흙둑이 그렇게 생겼다 — 흰 관에 얼룩진 옆구리.
    const idxCrest: number[] = [];
    for (let j = 0; j < rows; j++)
      for (let i = 0; i < W - 1; i++) {
        const a = j * W + i;
        const q = [a, a + W, a + 1, a + 1, a + W, a + W + 1];
        if (Math.max(lane[i][1], lane[i + 1][1]) < 0.34) idxCrest.push(...q);
        else idx.push(...q);
      }
    const nCrest = idxCrest.length;
    idx.unshift(...idxCrest);
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    g.setIndex(idx);
    g.addGroup(0, nCrest, 0);
    g.addGroup(nCrest, idx.length - nCrest, 1);
    g.computeVertexNormals();
    // 감는 방향이 틀리면 통째로 검은 판이 된다 — 언덕에서 이미 한 번 겪었다
    const nn = g.getAttribute('normal');
    let sum = 0;
    for (let k = 0; k < nn.count; k++) sum += nn.getY(k);
    if (sum < 0) {
      const ix = Array.from(g.getIndex()!.array);
      for (let k = 0; k < ix.length; k += 3) [ix[k + 1], ix[k + 2]] = [ix[k + 2], ix[k + 1]];
      g.setIndex(ix);
      g.computeVertexNormals();
    }
    // 둑 표면 결. uv 가 x/3, z2/3 이므로 3m 마다 한 장 — 눈밭(4m)과 비슷한 결이다
    const cwyTex = snowTex.clone();
    cwyTex.repeat.set(1.6, 1.6);
    cwyTex.needsUpdate = true;
    const crestMat = applySnowAccum(
      new THREE.MeshStandardMaterial({
        color: '#98a7ba', roughness: 0.95,
        map: cwyTex, bumpMap: cwyTex, bumpScale: 0.14,
      }),
      { flat: 0.2, coverage: 0.97, edge: 0.1, scale: 1.4, color: '#dbe6f4' },
    );
    const flankMat = applySnowAccum(
      // 옆비탈도 「흙 한 판」이면 같은 실패다. 노이즈 칸을 0.9m 로 줄여서
      // 드러나는 흙이 **자국**이 되게 하고, 결을 넣어 판때기를 깬다
      new THREE.MeshStandardMaterial({
        color: '#615a51', roughness: 0.97,
        map: cwyTex, bumpMap: cwyTex, bumpScale: 0.18,
      }),
      { flat: 0.28, coverage: 0.9, edge: 0.2, scale: 1.1 },
    );
    crestMat.name = 'cwy-crest'; flankMat.name = 'cwy-flank';
    const mesh = new THREE.Mesh(g, [crestMat, flankMat]);
    mesh.receiveShadow = true;
    vg.add(mesh);

    // 둑 옆비탈에 돌을 박는다. 아래쪽은 높이가 0 이라 비탈이 없으므로 t 를 0.12 부터
    for (let k = 0; k < 46; k++) {
      const t = 0.12 + hash(k, 0, 91) * 0.88;
      const h2 = cwy.yHigh * t + SH;
      const sd2 = k % 2 ? 1 : -1;
      const bx2 = sd2 < 0 ? cwy.x1 * RW : cwy.x2 * RW;
      const out2 = 0.15 + hash(k, 1, 17) * 0.7;
      const shX = (sd2 < 0 ? cwy.x1 : cwy.x2);
      const x2 = bx2 + (shX - bx2) * Math.min(1, out2 * 3) + sd2 * Math.max(0, out2 - 0.33) * 1.5 * bt * h2;
      putRock(x2, h2 * (1 - out2), cwy.zLow + dz * t + (hash(k, 2, 29) - 0.5) * 1.2,
        0.15 + hash(k, 3, 37) ** 2.0 * 0.6, k + 7);
    }
  }

  {
    const rg = mergeGeometries(rockParts, false);
    for (const geo of rockParts) geo.dispose();
    if (rg) {
      /**
       * **문턱 0.5 는 돌에 눈을 한 톨도 안 얹혔다.**
       *
       * 정이십면체 뚜껑은 카메라를 향한 면이 거의 서 있어서 N.y 가 0.2~0.3 이다.
       * `smoothstep(0.5, 1, 0.25)` 은 0 이므로 화면에 나오는 면은 전부 맨돌이고,
       * 그래서 비탈에 **갈색 판지 조각**이 널린 그림이 됐다 (`c3_언덕갈색판.png`).
       * 20cm 쌓인 눈밭의 돌은 그렇게 안 생겼다 — 위가 하얗고 옆구리만 드러난다.
       *
       * 문턱을 0.18 로 내리면 45도 면이 0.71 로 눈을 받고 수직에 가까운 면은
       * 여전히 0 이다. 즉 「눈 쓴 돌」이 되고 판지 조각이 사라진다.
       */
      const rm = new THREE.Mesh(rg, applySnowAccum(
        // #4c4b50 은 눈밭(화면값 157) 옆에서 **검정**으로 읽혔다. 밤의 화강암은
        // 검은 게 아니라 달빛을 받는 회갈색이다 — 두 단 올린다
        new THREE.MeshStandardMaterial({ color: '#6d675f', roughness: 0.93 }),
        { flat: 0.18, coverage: 0.88, edge: 0.2, scale: 0.9, color: '#ccd8ea', bump: 0.3 },
      ));
      rm.receiveShadow = true;
      vg.add(rm);
    }
  }

  /* --- 길 --- */
  // 눈 덮인 통짜 평면 위에서는 어디로 가야 하는지가 안 읽힌다. 실제로 조감도를
  // 보고 나온 첫 마디가 「어디가 길이냐」였다.
  //
  // **바닥보다 살짝 위(y=0.015)에 깐다.** 같은 높이면 z-fighting 으로 깜빡인다.
  // 언덕길은 기울어져 있어 네 귀퉁이 높이가 다르므로 사각형 하나를 직접 만든다.
  if (ROADS.length) {
    // **민무늬 판은 길이 아니다.** map 이 없어 노면이 균일한 밝은 회색으로 칠해졌고,
    // 옆의 새 눈과 톤이 같아 화면에서 통째로 사라졌다. 밟혀 다져진 눈은 새 눈보다
    // 어둡고 결이 있다 — 지면 텍스처를 촘촘히 깔고 한 단 어둡게 간다.
    // **결의 밀도가 노면을 새까맣게 만들었다.** 18m 길에 26번을 반복하면 타일
    // 하나가 70cm 다 — 지면(4m/타일)보다 여섯 배 촘촘하다. bumpMap 은 화면
    // 미분으로 법선을 흔들기 때문에 이 주파수에서는 노면 법선의 절반이 서쪽
    // 달빛을 등져 버린다. 색은 #aab7c6(밝은 회청)인데 화면에 찍힌 값은
    // **rgb(45,40,59)** — 옆의 눈밭 157 의 1/3 이었다. 아스팔트 활주로다.
    // 지면과 같은 결 밀도로 낮추고 요철은 눈 쌓임 노이즈에 맡긴다.
    const roadTex = snowTex.clone();
    roadTex.repeat.set(1.2, 9);
    roadTex.needsUpdate = true;
    //
    // 다만 **아스팔트가 되면 안 된다.** #8e9cad 로 깔았더니 눈길이 아니라 갓
    // 포장한 도로였다. 다져진 눈은 새 눈보다 어둡되 여전히 눈이다 — 바탕을
    // 눈 쪽으로 올리고, **어두운 건 바퀴가 지난 두 줄**이 맡는다. 그 대비가
    // 「차가 몇 번 지나간 눈길」을 한 번에 말한다.
    //
    // 그리고 **색으로 어둡게 만들지 않는다.** 다져진 눈길은 「어두운 띠」가 아니라
    // 「눈이 밟혀 군데군데 흙이 드러난 면」이다. 그러니 바탕은 흙으로 두고
    // 눈 쌓임 규칙이 그 위를 덮게 한다 — 덮는 비율만 다르게 주면 노면과
    // 바퀴 자국이 **같은 재료의 다른 상태**가 된다. 색을 두 개 고르는 것과
    // 결과가 전혀 다르다: 경계가 노이즈라 자로 그은 차선이 안 나온다.
    const packed = applySnowAccum(
      new THREE.MeshStandardMaterial({
        color: '#6f6a63', roughness: 0.9,
        map: roadTex, bumpMap: roadTex, bumpScale: 0.1,
      }),
      { flat: 0.1, coverage: 0.86, edge: 0.22, scale: 0.95, color: '#ccd8e6' },
    );
    //
    // **바퀴 자국을 #5c554c 로 깔았더니 화면값이 rgb(0,0,0) 이었다.** 달이 서쪽
    // 낮은 곳에 하나뿐이라 수평면이 받는 빛이 애초에 약한데, 거기에 알베도까지
    // 0.08 로 떨어뜨리면 남는 게 없다. 눈길의 바퀴 자국은 검정이 아니라
    // **한 단 회색** 이다 — 바탕을 밝은 흙으로 올리고 눈도 절반은 남긴다.
    const rutMat = applySnowAccum(
      new THREE.MeshStandardMaterial({
        color: '#88817a', roughness: 0.85,
        map: roadTex, bumpMap: roadTex, bumpScale: 0.14,
      }),
      { flat: 0.1, coverage: 0.55, edge: 0.24, scale: 1.5, color: '#b3bfcf' },
    );
    packed.name = 'road-packed'; rutMat.name = 'road-rut';
    const quad = (x1: number, z1: number, x2: number, z2: number, y1: number, y2: number, lift: number) => {
      const g2 = new THREE.BufferGeometry();
      // z1 쪽 두 점이 y1, z2 쪽 두 점이 y2.
      //
      // **감는 방향이 반대였다.** [0,2,1, 0,3,2] 로 감으면 법선이 (0,-108,-21.6)
      // — 아래를 본다. FrontSide 기본값이라 위에서 보면 통째로 컬링돼서
      // **마을 길 전체가 한 번도 화면에 나온 적이 없었다.** 「어디가 길이냐」의
      // 진짜 답이 이것이었다. 리프트를 올리든 색을 바꾸든 안 보이는 게 당연했다.
      g2.setAttribute('position', new THREE.Float32BufferAttribute([
        x1, y1 + lift, z1, x2, y1 + lift, z1, x2, y2 + lift, z2, x1, y2 + lift, z2,
      ], 3));
      g2.setIndex([0, 1, 2, 0, 2, 3]);
      g2.computeVertexNormals();
      return g2;
    };
    const roadParts: THREE.BufferGeometry[] = [];
    const rutParts: THREE.BufferGeometry[] = [];
    const bermParts: THREE.BufferGeometry[] = [];
    /**
     * 둑에는 **눈 재질을 그냥 쓰면 안 된다.**
     *
     * 세 바퀴를 흰 판때기로 보낸 진짜 이유가 이거였다 — 둑 전체가 `snowMat2`
     * 한 장이라 삽날이 낸 수직 면과 위에 얹힌 눈이 **똑같이 새하얬다.** 그러면
     * 아무리 모양을 흔들어도 플라스틱 리본이다.
     *
     * 길가 눈둑은 위만 하얗다. 옆구리는 제설차가 튀긴 흙과 모래로 잿빛이다.
     * 그러니 눈 쌓임 규칙을 태워서 **면의 방향이 색을 정하게** 한다:
     * 윗면(N.y≈0.98)은 통째로 눈, 삽날 면(N.y≈0.35)은 문턱 아래라 잿빛 그대로.
     * 그 대비가 곧 「파인 골」이다.
     */
    //
    // **다만 삽날 면을 새까맣게 두면 더 나쁘다.** flat 을 0.45 로 잡았더니 수직에
    // 가까운 안쪽 면(N.y≈0.3)이 문턱 아래로 떨어져 눈이 하나도 안 얹혔고, 서쪽
    // 달빛도 등지는 면이라 반구광만 남아 **거의 0에 가까운 검은 골**이 됐다.
    // 길 한가운데서 보면 길이 아니라 도랑 두 줄 사이의 심연으로 읽힌다.
    // 실제 제설 흔적은 잿빛이지 검정이 아니다 — 문턱을 낮춰 옅은 눈가루를
    // 남기고 바탕색도 한 단 올린다.
    //
    // **05 컷 아래를 먹던 크림색 판때기가 이 둑이었다**(화면에 광선을 쏴서
    // 재질 이름을 받았다 — `#a9adb4`, 304 삼각형). 색은 문제가 아니었다:
    // 가로등 불빛을 받은 회색이 크림으로 보인 것뿐이다. 문제는 **눈 노이즈
    // 한 칸이 3.3m**(scale 0.3)라서, 카메라 2m 앞의 둑 전체가 한 칸 안에
    // 통째로 들어간다는 것이다. 한 칸 = 한 값 = 균일한 판. 코앞에 오는
    // 재질은 칸이 **수십 cm** 여야 한다.
    const bermTex = snowTex.clone();
    bermTex.repeat.set(1, 1);
    bermTex.needsUpdate = true;
    const bermMat = applySnowAccum(
      new THREE.MeshStandardMaterial({
        color: '#a9adb4', roughness: 0.95,
        map: bermTex, bumpMap: bermTex, bumpScale: 0.12,
      }),
      { flat: 0.2, coverage: 0.9, edge: 0.14, scale: 1.4, color: '#dbe6f2' },
    );
    /**
     * 교차로에서는 둑이 **끊겨야 한다.**
     *
     * 대로에 둑을 세우면 그 둑이 환상로 두 갈래를 가로질러 45cm 짜리 턱으로
     * 막는다. 실제 제설은 교차로를 열어 두고, 열려 있는 그 자리가 곧
     * 「여기서 갈라진다」는 표시다 — 막으면 정보가 사라지는 게 아니라
     * 거꾸로 된 정보가 들어간다.
     *
     * 그래서 둑 마루가 **다른 길 위**에 오면 높이를 0 으로 내리고, 그 길에서
     * 1.4m 떨어지는 동안 원래 높이로 돌아온다. 판정은 사각형까지의 거리 하나다.
     */
    const rects = ROADS.map((q) => ({
      x1: Math.min(q.x1, q.x2), x2: Math.max(q.x1, q.x2),
      z1: Math.min(q.z1, q.z2), z2: Math.max(q.z1, q.z2),
    }));
    /**
     * **둑이 건물 안으로 들어오면 안 된다.** 조합센터 남쪽 길(z≈−30)의 둑이
     * 건물 남벽(z=−28)을 넘어 주방 싱크와 기록보관실 뒤쪽을 눈으로 덮었다
     * (2026-09-06, `__dbg.pick` 으로 #a9adb4 · 148×0.8×62 배치를 확인했다).
     * 둑 폭이 길 가장자리에서 1.5m 넘게 퍼지는데 길과 벽 사이가 그보다 좁은
     * 자리가 있다. 건물 발자국(+0.2m) 안에 드는 정점은 높이를 0 으로 내린다.
     */
    const bRects = BUILDINGS.map((q) => ({
      x1: Math.min(q.x1, q.x2) - 0.2, x2: Math.max(q.x1, q.x2) + 0.2,
      z1: Math.min(q.z1, q.z2) - 0.2, z2: Math.max(q.z1, q.z2) + 0.2,
    }));
    const outsideBuildings = (x: number, z: number) => {
      for (const q of bRects) if (x > q.x1 && x < q.x2 && z > q.z1 && z < q.z2) return 0;
      return 1;
    };
    const openAt = (self: number, x: number, z: number) => {
      let f = 1;
      for (let n = 0; n < rects.length; n++) {
        if (n === self) continue;
        const q = rects[n];
        const dx = Math.max(q.x1 - x, 0, x - q.x2);
        const dz = Math.max(q.z1 - z, 0, z - q.z2);
        f = Math.min(f, Math.min(1, Math.hypot(dx, dz) / 1.4));
        if (f <= 0) return 0;
      }
      return f;
    };
    for (let ri = 0; ri < ROADS.length; ri++) {
      const r = ROADS[ri];
      const y1 = r.y1 ?? 0, y2 = r.y2 ?? 0;
      /**
       * 노면을 지면에서 띄우는 높이.
       *
       * 비탈 쪽이 **0.24** 였다. 「비탈판(두께 0.24) 위를 지나므로 그만큼
       * 띄워야 한다」는 계산이었는데, 그 판은 이미 없다 — 성토 둑길
       * (`causeways`)로 바뀌면서 **노면 꼭짓점이 램프 중심선 그 자체**가 됐다
       * (`out=0, add=0` → `y = h`). 그런데 리프트만 그대로 남아서, 언덕길이
       * 산비탈 위 **21cm 공중에 뜬 회색 리본**이었다(광선으로 재 봤다:
       * z=-46.95 에서 노면 3.63 · 둑 표면 3.44 · 보행면 3.41).
       *
       * 더 나쁜 것은 이음매다. 언덕 위 평지 구간은 `y1===y2` 라 0.015 로 깔리니,
       * 비탈이 끝나는 z=-48 에서 노면이 **22.5cm 뚝 떨어진다** — 한서진 집
       * 현관 바로 앞을 가로지르는 그 턱이 Chris 가 「경계선이 이상하다」고
       * 짚은 선이다. 보행면은 멀쩡히 이어져 있었다(3.4→3.6, 1m 당 0.2m).
       * 즉 순전히 노면 데칼만의 문제다.
       *
       * 0.07 은 둑 노면 가로에 걸린 y 흔들림(±0.03)을 넘기는 최소값이다.
       * 남는 이음매 턱은 5.5cm 라 3m 거리에서 1° 다.
       */
    const lift = y1 === y2 ? 0.015 : 0.07;
      roadParts.push(quad(r.x1, r.z1, r.x2, r.z2, y1, y2, lift));

      // 치운 눈은 **길 옆에 쌓인다.** 이 둔덕이 없으면 길은 눈밭에 그린 색띠일
      // 뿐이고, 그래서 눈이 몇 cm 쌓였는지가 화면 어디에도 안 나온다.
      // 둔덕 하나가 「여기 눈이 이만큼 쌓여 있고 누군가 치웠다」를 동시에 말한다.
      if (r.berm) {
        const w0 = r.x2 - r.x1, d0 = r.z2 - r.z1;
        const alongZ0 = Math.abs(d0) >= Math.abs(w0);
        // **둑을 세우지 말고 길을 파라.**
        //
        // 두 번 틀렸다. 처음엔 0.75 x 0.42 x 18m 상자 하나 — 다이빙대가 됐다.
        // 다음엔 그걸 2m 마디로 끊고 폭·높이를 흔들었는데, 여전히 **길 옆에 놓인
        // 별개의 물건**이라 가드레일로 읽혔다. 마디로 끊는 건 값을 만진 것이지
        // 접근을 바꾼 게 아니었다.
        //
        // 제설차가 지나간 길은 「둑이 서 있는 길」이 아니라 **눈을 걷어내 파인 골**
        // 이다. 그러니 단면을 그렇게 만든다: 길 가장자리에서 45cm 수직으로 서고
        // (삽날이 낸 면), 거기서 2m 넘게 **완만히 흘러내려** 눈밭에 스며든다.
        // 바깥에 수직면이 없으면 물건이 아니라 지형이 된다.
        //
        // **이 단면을 남북 길에만 만들어 뒀던 것이 대로가 안 보이던 이유다.**
        // 동서로 뻗은 길(대로·뒤편 가로로)은 `else` 로 빠져서 낮은 상자 두 개를
        // 받았고, 그건 눈밭 위의 회색 띠 이상이 못 됐다. 길이 보이는 것은 색이
        // 아니라 **깊이** 다 — 그러니 축을 일반화한다. 길 방향을 u, 가로를 v 로
        // 두고 좌표를 만들 때만 축을 되돌린다.
        const L = alongZ0 ? d0 : w0;                          // 길 방향(부호 있음)
        const CW = alongZ0 ? Math.abs(w0) : Math.abs(d0);     // 길 폭
        const cv = alongZ0 ? (r.x1 + r.x2) / 2 : (r.z1 + r.z2) / 2;
        const u0 = alongZ0 ? r.z1 : r.x1;
        /**
         * **단면을 치운 눈의 부피에서 낸다.**
         *
         * 폭·높이를 손으로 적어 놨더니(높이 0.2~0.56, 폭 1.6) 둑이 **1.6m 를
         * 0.4m 로 흘러내리는 14° 경사**가 됐다. 그건 눈더미가 아니라 눈밭에
         * 오려 붙인 흰 종이다 — 교문 앞 컷의 그 두 조각이 정확히 이것이었다.
         * 줄을 하나 더 넣어 볼록하게 만드는 것으로는 안 됐다. 애초에 **폭이
         * 높이의 네 배**라 어떤 곡률을 줘도 수평면이다.
         *
         * 삽으로 길을 내면 나오는 눈은 정해져 있다: `길폭 x 적설 / 2` 가 한쪽
         * 둑의 단면적이다. 여기에 눈의 안식각(약 38°, tanθ≈0.78)을 걸면 높이와
         * 폭이 **동시에** 정해진다 — 값 두 개를 따로 고를 여지가 없어진다.
         * 이러면 좁은 길은 작고 낮은 둑, 대로는 크고 높은 둑이 자동으로 나온다.
         *
         * 차가 다니는 길(`ruts`)은 제설차가 여러 번 밀어 더 쌓이므로 1.5배.
         */
        const SNOW_D = 0.2, REPOSE = 0.78;
        const vol = ((CW * SNOW_D) / 2) * (r.ruts ? 1.5 : 1);
        const hB = Math.sqrt(vol * REPOSE);    // 마루 높이
        const wB = 2 * Math.sqrt(vol / REPOSE); // 발치에서 바깥끝까지
        /** (가로 v, 높이 y, 길이 u) → 월드 좌표. 축을 되돌리는 유일한 지점 */
        const P = (v: number, y: number, u: number): [number, number, number] =>
          (alongZ0 ? [v, y, u] : [u, y, v]);
        // **마디를 1.5m 로 잡았더니 코앞에서 매끈한 각목이었다.** 카메라가 길
        // 한가운데 서면 둑까지 3m 다 — 그 거리에서 눈으로 보이려면 요철이
        // 미터가 아니라 **수십 cm** 단위여야 한다
        const stations = Math.max(6, Math.round(Math.abs(L) / 0.8) + 1);
        const frac = (n: number) => n - Math.floor(n);
        for (const s3 of [-1, 1]) {
          const pos: number[] = [], uv: number[] = [], idx: number[] = [];
          const edge = cv + (s3 * CW) / 2;
          for (let k = 0; k < stations; k++) {
            const p = k / (stations - 1);
            const j1 = frac(Math.sin(k * 12.9898 + s3 * 78.233) * 43758.5453);
            const j2 = frac(Math.sin(k * 39.3468 + s3 * 11.135) * 24634.6345);
            const uk = u0 + p * L;
            const yk = y1 + p * (y2 - y1) + lift;
            // 느린 기복 + 잔요철 두 겹. 잔요철만 주면 지글거리는 잡음이 되고,
            // 기복만 주면 다시 매끈한 물결 판이 된다
            const wave = 0.5 + 0.5 * Math.sin(p * 17 + s3 * 2.1);
            const pk = P(edge + s3 * 0.4, 0, uk);
            /**
             * 끝을 재운다. 대로 서쪽 컷에서 **둑이 창처럼 뾰족하게 잘려** 눈밭
             * 위에 흰 종이 조각 두 장이 놓인 꼴이었다 — 길이 x=-60 에서 끝나는데
             * 둑은 단면을 그대로 드러낸 채 끊겼기 때문이다. 양 끝 2.5m 를 0 으로
             * 흘려서 눈밭에 잠기게 한다.
             */
            /**
             * 재우는 길이를 **길이에 비례**시킨다. 2.5m 로 고정해 놨더니 8m 짜리
             * 분교 진입로는 양 끝 2.5m 씩, 즉 **62%가 재워져** 가운데만 부푼
             * 나뭇잎 모양 두 장이 됐다(`c2_분교흰조각.png`). 긴 대로에 맞춘
             * 값을 짧은 길에 그대로 쓴 것이 원인이다.
             */
            const tapLen = Math.min(2.4, Math.abs(L) * 0.18);
            const tap = Math.min(1, (p * Math.abs(L)) / tapLen, ((1 - p) * Math.abs(L)) / tapLen);
            const pkOut = P(edge + s3 * 1.6, 0, uk);   // 둑 바깥끝 자리로 건물 침범을 본다
            const op = openAt(ri, pk[0], pk[2]) * tap * outsideBuildings(pkOut[0], pkOut[2]);   // 교차로·양 끝·건물 안이면 0
            const hIn = hB * (0.84 + wave * 0.26 + j1 * 0.14) * op;   // 삽날 면 높이
            /**
             * **op 가 0 이면 폭도 0 이다.** 높이만 0 으로 내리고 폭 0.12 + 발치·마루
             * 흔들림을 남겼더니, 건물 안으로 들어간 구간에 **바닥에 붙은 흰 띠
             * 0.3m** 가 남았다 — 조합센터 공동주방 북벽 밑에 그 띠가 깔렸다
             * (Chris 「여기 여전히 눈이 넘은듯」). 높이 0 의 둑은 없는 둑이다.
             */
            const W = (0.12 + wB * (0.86 + wave * 0.2 + j2 * 0.18)) * op;
            // 발치도 흔든다 — 길 가장자리가 자로 그은 직선이면 둑이 아니라 연석이다
            const foot = (j1 - 0.5) * 0.35 * op;
            const crest = (foot + 0.1 + j2 * 0.22) * op;
            /**
             * 발치 · 마루 · **치마 중턱** · 바깥끝 — 네 줄.
             *
             * 세 줄일 때 치맛자락이 마루에서 바깥끝까지 1.5m 를 **한 판**으로
             * 내려왔다. 기울기가 11도라 사실상 수평이고, 그 면이 달빛을 고르게
             * 받아 눈밭에 흰 종이를 오려 붙인 꼴이 됐다(교문 앞 컷에서 제일
             * 심했다). 중간에 줄을 하나 넣어 **볼록하게** 만든다 — 제설차가
             * 밀어 쌓은 눈은 안쪽이 서고 바깥이 둥글게 퍼진다.
             */
            const midV = crest + (W - crest) * 0.45;
            pos.push(...P(edge + s3 * (foot - 0.05), yk + 0.02, uk));
            pos.push(...P(edge + s3 * crest, yk + hIn, uk));
            pos.push(...P(edge + s3 * midV, yk + hIn * 0.42 + (j2 - 0.5) * 0.06, uk));
            // 바깥 끝은 지면보다 **1cm 아래**로 박는다. 위로 띄웠더니 치맛자락이
            // 달빛을 따로 받아 흰 판 한 장이 눈밭에 얹힌 것처럼 보였다
            pos.push(...P(edge + s3 * W, yk - 0.01 + (j1 - 0.5) * 0.06, uk));
            // 눈 결이 보이게 밀도를 올린다 — 2m 를 uv 한 칸에 늘여 놨더니
            // 둑 등판이 무늬 없는 흰 종이였다
            for (let t = 0; t < 4; t++) uv.push(t * 0.6, p * Math.abs(L) * 0.5);
          }
          for (let k = 0; k < stations - 1; k++) {
            for (let row = 0; row < 3; row++) {
              const a = k * 4 + row, b = a + 1, c = a + 4, d = a + 5;
              idx.push(a, b, d, a, d, c);
            }
          }
          const g3 = new THREE.BufferGeometry();
          g3.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
          g3.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
          g3.setIndex(idx);
          g3.computeVertexNormals();
          /**
           * **감는 방향을 손으로 맞추지 않는다.** 예전엔 `s3 * Math.sign(d0) < 0`
           * 로 뒤집었는데, 축을 일반화하니 경우가 넷(축 2 × 부호 2)이 되고
           * 그중 둘은 틀린다 — 틀리면 면이 통째로 컬링돼서 **둑이 사라진다**.
           * 이미 같은 사고로 마을 길 전체가 안 보인 적이 있다.
           * 그러니 만들고 나서 법선을 **재 본다**: 위를 향해야 할 면인데
           * 평균 N.y 가 음수면 인덱스를 뒤집는다. 경우의 수를 셀 필요가 없다.
           */
          const nrm = g3.getAttribute('normal');
          let ny = 0;
          for (let v = 0; v < nrm.count; v++) ny += nrm.getY(v);
          if (ny < 0) {
            const ix = g3.getIndex()!.array as unknown as number[];
            for (let t = 0; t < ix.length; t += 3) { const tmp = ix[t + 1]; ix[t + 1] = ix[t + 2]; ix[t + 2] = tmp; }
            g3.getIndex()!.needsUpdate = true;
            g3.computeVertexNormals();
          }
          bermParts.push(g3);
        }
      }
      if (!r.ruts) continue;
      // 바퀴 자국 둘. 긴 축을 따라 폭의 22% 지점에 넣는다.
      //
      // **한 장짜리 긴 사각형은 바퀴 자국이 아니라 차선이다.** 폭 0.6m 짜리
      // 직선 두 줄을 18m 통으로 깔았더니 도로 도색으로 읽혔다. 실제 자국은
      // 더 좁고, 운전대를 따라 좌우로 흔들린다 — 마디로 끊어 중심을 흔든다.
      const w = r.x2 - r.x1, d = r.z2 - r.z1;
      const alongZ = Math.abs(d) >= Math.abs(w);
      const SEG = 14;
      for (const s2 of [-1, 1]) {
        const wob = (k: number) => Math.sin(k * 0.9 + (s2 > 0 ? 1.7 : 0)) * 0.055
          + Math.sin(k * 2.3 + 0.4) * 0.025;
        for (let k = 0; k < SEG; k++) {
          const t0 = k / SEG, t1 = (k + 1) / SEG;
          if (alongZ) {
            const c0 = (r.x1 + r.x2) / 2 + s2 * w * 0.22 + w * wob(k);
            const c1 = (r.x1 + r.x2) / 2 + s2 * w * 0.22 + w * wob(k + 1);
            const hw = Math.abs(w) * 0.062;
            const zA = r.z1 + d * t0, zB = r.z1 + d * t1;
            const yA = y1 + (y2 - y1) * t0, yB = y1 + (y2 - y1) * t1;
            // 마디마다 사다리꼴 — quad() 는 z1/z2 두 줄만 받으므로 직접 만든다
            const g4 = new THREE.BufferGeometry();
            g4.setAttribute('position', new THREE.Float32BufferAttribute([
              c0 - hw, yA + lift + 0.005, zA, c0 + hw, yA + lift + 0.005, zA,
              c1 + hw, yB + lift + 0.005, zB, c1 - hw, yB + lift + 0.005, zB,
            ], 3));
            g4.setIndex([0, 1, 2, 0, 2, 3]);
            g4.computeVertexNormals();
            rutParts.push(g4);
          } else {
            const cz = (r.z1 + r.z2) / 2 + s2 * d * 0.22 + d * wob(k);
            const hd = Math.abs(d) * 0.062;
            rutParts.push(quad(r.x1 + w * t0, cz - hd, r.x1 + w * t1, cz + hd, y1, y2, lift + 0.005));
          }
        }
      }
    }
    for (const [parts, mat] of [[roadParts, packed], [rutParts, rutMat], [bermParts, bermMat]] as const) {
      const merged = mergeGeometries(parts, false);
      for (const geo of parts) geo.dispose();
      if (merged) vg.add(new THREE.Mesh(merged, mat));
    }
  }

  // 마당의 나무 — 창 바로 밖에 뭔가 있어야 거리가 읽힌다
  /**
   * **`#2a2622` 는 화면에서 `rgb(0,0,0)` 이 된다.**
   *
   * 언덕길 컷에서 나무마다 밑동에 검은 사각형이 붙어 있어 데칼 사고인 줄 알았는데,
   * 픽셀을 뽑으니 정확히 0,0,0 이었다 — 이 씬에서 제일 어두운 벽도 (78,89,114) 다.
   * 범인은 albedo 와 GTAO 의 곱이다. `#2a2622` 는 선형으로 0.023 인데 GTAO 를
   * `blendIntensity 1.15` 로 걸어 놨으니 몸통이 지면에 닿는 골에서 가림값이 1 을
   * 넘어 남는 게 없다. 잎(#3b4a43)은 두 배 밝아서 살아남았고 몸통만 죽었다.
   *
   * GTAO 를 낮추면 씬 전체가 뜬다. 어두운 재질 쪽을 올린다 — 눈에 둘러싸인
   * 밤의 나무껍질은 사방에서 되받는 빛을 받아 실제로 이 정도로 밝다.
   */
  // 한 바퀴 더: `#4c433a`(선형 0.075)로 올린 뒤에도 찍어 보니 밑동은 여전히
  // rgb(7,6,16) 이었다. 밤 씬에 GTAO 1.15 면 0.075 도 안 남는다 — 실측으로
  // 다시 두 배 올린다. 눈밭에 둘러싸인 껍질은 이 정도로 밝다.
  const trunk = new THREE.MeshStandardMaterial({ color: '#6f6254', roughness: 0.95 });
  /**
   * **잎은 눈이 아니다.** 예전에는 나무 한 그루가 원뿔 **하나**였고 그 원뿔이
   * 눈 재질(#aebdcd)이었다 — 그래서 대로 컷 앞의 나무가 파란 막대사탕으로
   * 나왔다. 잣나무는 몸이 거의 검고, 눈은 **가지 위에만** 얹힌다.
   */
  //
  // **다만 통째로 검으면 나무가 아니라 판지 오려낸 것이다.** 달빛이 서쪽
  // 하나뿐이라 원뿔의 동쪽 반은 rgb(0,0,0) 으로 떨어졌고, 비탈 컷 좌우가
  // 검은 삼각형 넷으로 막혔다. 잣나무 가지에는 눈가루가 앉는다 — 위를 향한
  // 면에만 옅게 얹히게 해서 **면의 방향이 밝기를 만들게** 한다.
  const needle = applySnowAccum(
    new THREE.MeshStandardMaterial({ color: '#3a4a3e', roughness: 0.95 }),
    { flat: 0.5, coverage: 0.62, edge: 0.24, scale: 1.1, color: '#8ea1b6' },
  );
  const snowCap = new THREE.MeshStandardMaterial({ color: '#bccad7', roughness: 0.9 });
  // 나무 하나가 메시 둘이다. 마당 열 그루 + 방풍림 오십 그루면 메시 120개,
  // 드로우콜 120 이 매 프레임 그대로 나간다. 전부 정적이고 재질이 둘뿐이라
  // 통째로 합친다.
  const trunkParts: THREE.BufferGeometry[] = [];
  const needleParts: THREE.BufferGeometry[] = [];
  const capParts: THREE.BufferGeometry[] = [];
  // 소나무 줄기는 붉은 갈색이다 — 잣나무·참나무의 회갈색과 같이 합치면 소나무가 아니다
  const pineTrunk = new THREE.MeshStandardMaterial({ color: '#5c4232', roughness: 0.92 });
  const pineTrunkParts: THREE.BufferGeometry[] = [];
  /* **파츠 통을 바꿔 끼울 수 있게** (2026-09-14, Chris: 「창문이랑 나무도 같은 방식으로」).
   * 편집 가능한 나무(`OUTDOOR.trees`, 30그루)는 방풍림과 **다른 통**에 담아 제 그룹으로
   * 굽는다 — 에디터에서 한 그루 옮길 때 그 그룹만 다시 지으면 되고, 방풍림 수백 그루와
   * 지형은 손도 안 댄다. 예전엔 세계를 통째로 다시 지어 렉이었다. */
  let T = { trunk: trunkParts, pine: pineTrunkParts, needle: needleParts, cap: capParts };
  /**
   * 침엽수 한 그루 — 층을 겹쳐 쌓고 각 층 **윗부분에만** 눈을 씌운다.
   *
   * 눈 원뿔의 밑반지름은 잎 원뿔의 그 높이 반지름보다 **조금 커야** 한다.
   * 정확히 같으면 두 면이 겹쳐 z-파이팅으로 지글거린다(0.60 → 0.63).
   */
  const rnd = (n: number) => {
    const v = Math.sin(n * 127.1 + 311.7) * 43758.5453;
    return v - Math.floor(v);
  };
  /**
   * **눈 더미 — 이 파일의 나무는 전부 이걸로 눈을 얹는다.** (2026-09-09 새로 짬)
   *
   * 전에는 매끈한 원뿔을 얹었다. 매끈한 원뿔은 어느 각도에서 봐도 **도형**이지
   * 「쌓인 것」이 아니다 — 쌓인 눈의 표시는 ① 가장자리가 제 무게로 처지고
   * ② 둘레가 울퉁불퉁하고 ③ 얹힌 것보다 조금 **넘쳐 나온다**, 이 셋이다.
   * 각도로 씨앗을 만들어 반지름을 흔들고 밑 링을 끌어내린다.
   */
  const snowMound = (r: number, hgt: number, seg: number, sd: number) => {
    const g = new THREE.ConeGeometry(r, hgt, seg, 3);
    const p = g.attributes.position;
    for (let k = 0; k < p.count; k++) {
      const vx = p.getX(k), vy = p.getY(k), vz = p.getZ(k);
      const rad = Math.hypot(vx, vz);
      if (rad < 1e-5) continue;
      const a = Math.atan2(vz, vx);
      const key = Math.round((a + 4) * 3.9);
      const lump = 0.84 + 0.3 * rnd(sd + key);
      p.setX(k, vx * lump);
      p.setZ(k, vz * lump);
      if (vy < -hgt * 0.15) p.setY(k, vy - hgt * 0.3 * rnd(sd + key * 1.7 + 5.3));
    }
    p.needsUpdate = true;
    g.computeVertexNormals();
    return g;
  };
  /**
   * **너덜너덜한 잎 치마.** 매끈한 원뿔 가장자리는 종이를 오린 것이다. 밑 링의
   * 반지름을 각도마다 ±18% 흔들고 끝을 끌어내려, 가지 끝이 들쭉날쭉한 실루엣을 만든다.
   */
  const raggedSkirt = (r: number, hgt: number, seg: number, sd: number) => {
    const g = new THREE.ConeGeometry(r, hgt, seg, 2);
    const p = g.attributes.position;
    for (let k = 0; k < p.count; k++) {
      const vx = p.getX(k), vy = p.getY(k), vz = p.getZ(k);
      const rad = Math.hypot(vx, vz);
      if (rad < 1e-5) continue;
      const key = Math.round((Math.atan2(vz, vx) + 4) * 4.7);
      const f = 0.82 + 0.36 * rnd(sd + key);
      p.setX(k, vx * f);
      p.setZ(k, vz * f);
      if (vy < -hgt * 0.2) p.setY(k, vy - hgt * 0.14 * rnd(sd + key * 2.1 + 9.7));
    }
    p.needsUpdate = true;
    g.computeVertexNormals();
    return g;
  };
  /**
   * **눈 쌓인 침엽수 — 3판 (2026-09-09).**
   *
   * 2판은 층 4~5개에 층마다 눈을 **링**으로 둘렀다. 확대하니 흰 띠·초록 띠가 번갈아
   * 나는 **줄무늬 파고다**였다(Chris: 「야 나무 봐바」). 실제 눈 쌓인 잣나무는
   * ① 층이 촘촘히 **겹쳐** 실루엣이 끊기지 않는 너덜너덜한 원뿔이고
   * ② 눈은 링이 아니라 가지 끝에 **반점**으로 앉아 사이사이 잎이 어둡게 남는다.
   * 그래서 층을 8~11개로 겹치고, 눈은 층 가장자리에 덩어리 2~4개를 흩어 앉힌다.
   * 삼각형은 그루당 ~1,400 — 전부 정적 병합이라 드로우콜은 그대로 둘이다.
   */
  const conifer = (x: number, z: number, y0: number, h: number, seg: number, tiers: number, sd = 0) => {
    T.trunk.push(
      new THREE.CylinderGeometry(h * 0.03, h * 0.05, h * 0.75, 6).translate(x, y0 + h * 0.375, z),
    );
    T.cap.push(snowMound(h * 0.125, h * 0.17, 9, sd + 91.3).translate(x, y0 + h * 0.04, z));

    // 몸매: 가늘고 높은 것 ↔ 낮고 퍼진 것. 폭이 ±30% 갈리면 멀리서 구별된다
    const form = 0.78 + rnd(sd + 63.1) * 0.52;
    const n = Math.max(8, tiers + 5);
    for (let i = 0; i < n; i++) {
      const t = i / (n - 1);
      const jy = (rnd(sd + i * 3.1) - 0.5) * 0.06;
      const jr = 0.9 + 0.2 * rnd(sd + i * 7.7 + 1.3);
      const ja = rnd(sd + i * 5.3 + 2.9) * Math.PI * 2;
      const jd = h * 0.03 * rnd(sd + i * 11.9 + 4.1);
      const yb = y0 + h * (0.2 + 0.7 * t + jy);
      /* **아래층이 접시였다**(Chris 2026-09-09: 「너무 접시임」). 반지름이 h·0.30 이라
       * 밑층이 나무 높이의 60% 폭이었고, 층 높이 r·1.15 는 그 폭에 비해 낮아 납작했다.
       * 실제 잣나무 밑층 폭은 높이의 35~40% 다. 반지름을 0.21 로 줄이고 층 높이를
       * r·1.7 로 세워, 아래층이 접시가 아니라 **가파른 치마**가 되게 한다. */
      const r = h * 0.21 * form * (1 - 0.8 * t) * jr;
      const hh = r * 1.7 + h * 0.06;
      const ox = x + Math.cos(ja) * jd;
      const oz = z + Math.sin(ja) * jd;
      T.needle.push(raggedSkirt(r, hh, seg, sd + i * 13.1).translate(ox, yb + hh / 2, oz));

      // 눈 반점 — 층 비탈 위, 가장자리 쪽에 2~4개. 링을 두르지 않는다
      // 반지름의 26~48% 는 흰 베개였다 — 더 작게(18~32%), 더 많이(3~5개)
      // 「눈이 쌓여야 함」— 덩어리 4~7개, 반지름 24~42%. 그래도 링으로 이어지진 않는다
      const k = 4 + Math.floor(rnd(sd + i * 19.3) * 4);
      for (let m = 0; m < k; m++) {
        const a = rnd(sd + i * 23.7 + m * 4.3) * Math.PI * 2;
        const rr = r * (0.5 + 0.42 * rnd(sd + i * 29.1 + m * 6.1));
        const mr = r * (0.24 + 0.18 * rnd(sd + i * 31.7 + m * 8.9));
        // 원뿔 비탈의 그 반지름에서의 높이. 밑(rr=r)=yb, 꼭지(rr=0)=yb+hh
        const ys = yb + hh * (1 - rr / r);
        T.cap.push(
          snowMound(mr, mr * 0.7, 6, sd + i * 37.3 + m * 10.7)
            .translate(ox + Math.cos(a) * rr, ys + mr * 0.22, oz + Math.sin(a) * rr),
        );
      }
    }
    // 우듬지 — 눈을 뒤집어쓴다
    const rt = h * 0.3 * form * 0.18 + h * 0.02;
    T.cap.push(snowMound(rt, rt * 2.2, 6, sd + 41.7).translate(x, y0 + h * 0.92, z));
  };
  /**
   * **눈에 파묻힌 관목.** 마을 나무가 전부 어른 키라 크기 대비가 없었다 —
   * 허리 높이 덤불이 섞여야 나무가 커 보인다. 겨울 관목은 잔가지가 거의
   * 안 보이고 **눈 무더기에 잔가지 끝만 삐져나온** 모습이다.
   */
  const snowBush = (x: number, z: number, y0: number, sz: number, sd: number) => {
    for (let i = 0; i < 4; i++) {
      const a = rnd(sd + i * 2.7) * Math.PI * 2;
      const ln = sz * (0.5 + rnd(sd + i * 4.1) * 0.5);
      const g = new THREE.CylinderGeometry(sz * 0.02, sz * 0.035, ln, 4);
      g.translate(0, ln / 2, 0);
      g.rotateZ((0.3 + rnd(sd + i * 6.3) * 0.5) * (i % 2 ? 1 : -1));
      g.rotateY(a);
      g.translate(x, y0, z);
      T.trunk.push(g);
    }
    T.cap.push(snowMound(sz * 0.62, sz * 0.7, 9, sd + 7.7).translate(x, y0 + sz * 0.22, z));
    T.cap.push(
      snowMound(sz * 0.4, sz * 0.45, 8, sd + 23.1)
        .translate(x + sz * 0.3, y0 + sz * 0.12, z - sz * 0.22),
    );
  };
  /**
   * **강원 소나무 (Pinus densiflora) — 이 마을의 주인.** (2026-09-09, Chris: 「배경이 강원도잖아」)
   *
   * 산림청 임상 자료로 강원도는 소나무·신갈나무가 압도적이고 원뿔형 잣나무는 소수다.
   * 소나무는 원뿔이 아니다 — **굽은 붉은 줄기** 위에 가지가 옆으로 벌어져 **우산처럼
   * 퍼진 둥근 잎 뭉치**가 몇 개 얹힌다. 눈은 뭉치 위에 둥근 덩어리로 앉는다.
   * 줄기는 두 토막을 꺾어 잇고, 뭉치는 납작한 타원체 3~6개를 위쪽에 흩는다.
   */
  const redPine = (x: number, z: number, y0: number, h: number, sd: number) => {
    const bend = (rnd(sd + 1.1) - 0.5) * 0.5;
    const bendA = rnd(sd + 2.2) * Math.PI * 2;
    const h1 = h * 0.45, h2 = h * 0.35;
    const t1 = new THREE.CylinderGeometry(h * 0.022, h * 0.03, h1, 7);   // 0.046 은 지름 55cm — 마을 소나무가 아니었다
    t1.translate(0, h1 / 2, 0);
    t1.rotateZ(bend * 0.4);
    t1.rotateY(bendA);
    t1.translate(x, y0, z);
    T.pine.push(t1);
    // 첫 토막 끝 — 회전을 그대로 풀어 잇는다 (가지 날아다닌 사고의 교훈)
    const ex = x - Math.sin(bend * 0.4) * Math.cos(bendA) * h1;
    const ey = y0 + Math.cos(bend * 0.4) * h1;
    const ez = z + Math.sin(bend * 0.4) * Math.sin(bendA) * h1;
    const t2 = new THREE.CylinderGeometry(h * 0.014, h * 0.023, h2, 6);
    t2.translate(0, h2 / 2, 0);
    t2.rotateZ(-bend * 0.9);
    t2.rotateY(bendA);
    t2.translate(ex, ey - h * 0.02, ez);
    T.pine.push(t2);
    T.cap.push(snowMound(h * 0.11, h * 0.15, 8, sd + 91.3).translate(x, y0 + h * 0.035, z));

    /* 잎 뭉치. **매끈한 납작 타원체는 비행접시였다**(2026-09-09 컷) — 원경 소나무 숲이
     * 접시 꽂은 막대들이 됐다. 실제 소나무 잎 뭉치는 잔가지 끝 바늘잎 다발이 모인
     * **울퉁불퉁한 덩어리**고, 큰 것 하나가 아니라 작은 것 여럿이 겹친다.
     * 구의 정점을 각도 씨앗으로 ±22% 흔들어 너덜하게 하고, 5~9개를 겹쳐 놓는다.
     * 눈은 뭉치 위에 **뚜껑이 아니라 반점** 2~3개다. */
    const ragBlob = (cr: number, sdd: number) => {
      const g = new THREE.SphereGeometry(cr, 9, 7);
      const pp = g.attributes.position;
      for (let k = 0; k < pp.count; k++) {
        const vx = pp.getX(k), vy = pp.getY(k), vz = pp.getZ(k);
        const key = Math.round((Math.atan2(vz, vx) + 4) * 3.1) * 7 + Math.round((vy / cr + 1) * 2.5);
        const f = 0.78 + 0.44 * rnd(sdd + key);
        pp.setXYZ(k, vx * f, vy * f * 0.7, vz * f);
      }
      pp.needsUpdate = true;
      g.computeVertexNormals();
      return g;
    };
    const n = 5 + Math.floor(rnd(sd + 3.3) * 5);
    const cx = ex - Math.sin(-bend * 0.9) * Math.cos(bendA) * h2 * 0.6;
    const cz = ez + Math.sin(-bend * 0.9) * Math.sin(bendA) * h2 * 0.6;
    for (let i = 0; i < n; i++) {
      const a = rnd(sd + i * 4.7) * Math.PI * 2;
      const spread = h * (0.06 + 0.2 * rnd(sd + i * 6.1));
      const cr = h * (0.09 + 0.06 * rnd(sd + i * 8.3));
      const cy = y0 + h * (0.62 + 0.3 * rnd(sd + i * 9.9));
      const px = cx + Math.cos(a) * spread, pz = cz + Math.sin(a) * spread;
      needleParts.push(ragBlob(cr, sd + i * 15.1).translate(px, cy, pz));
      /* **눈이 쌓여야 한다**(Chris 2026-09-09). 반점 2~3개는 「눈발 날린 뒤」지 「밤새 쌓인 뒤」가
       * 아니었다. 뭉치 윗면을 덮는 눈 덮개(반지름 0.95) 하나 + 그 위 덩어리 2~3개. */
      /* 덮개 중심 0.36cr·높이 0.55cr 이면 꼭지가 0.64cr — 뭉치 꼭대기(너덜 배율 포함 ~0.85cr)
       * **속에 묻혀** 가까운 소나무가 여전히 검었다(컷 2026-09-09). 중심을 0.62cr 로 올려
       * 밑면 0.37cr·꼭지 0.87cr 로 뭉치 윗면을 감싼다. */
      capParts.push(
        snowMound(cr * 1.0, cr * 0.5, 8, sd + i * 21.7).translate(px, cy + cr * 0.62, pz),
      );
      const k = 2 + Math.floor(rnd(sd + i * 11.3) * 2);
      for (let m = 0; m < k; m++) {
        const ma = rnd(sd + i * 13.7 + m * 5.1) * Math.PI * 2;
        const mr = cr * (0.3 + 0.2 * rnd(sd + i * 17.9 + m * 3.3));
        const rr = cr * 0.45 * rnd(sd + i * 19.3 + m * 7.7);
        capParts.push(
          snowMound(mr, mr * 0.7, 6, sd + i * 12.7 + m * 9.1)
            .translate(px + Math.cos(ma) * rr, cy + cr * 0.9, pz + Math.sin(ma) * rr),
        );
      }
      const br = new THREE.CylinderGeometry(h * 0.006, h * 0.011, spread * 1.05 + cr * 0.3, 4);
      br.translate(0, (spread * 1.05 + cr * 0.3) / 2, 0);
      br.rotateZ(Math.PI / 2 - 0.35);
      br.rotateY(-a);
      br.translate(cx, cy - cr * 0.4, cz);
      pineTrunkParts.push(br);
    }
  };
  /**
   * **낙엽송 (일본잎갈나무) — 조림지.** 침엽수인데 겨울에 잎을 떨군다.
   * 곧고 가늘고 키가 크며, 짧은 잔가지가 층층이 수평으로 붙는다. 능선 쪽에
   * 줄지어 서면 강원도 산비탈이 된다. 눈은 잔가지 위에 얇은 선으로만.
   */
  const larch = (x: number, z: number, y0: number, h: number, sd: number) => {
    trunkParts.push(new THREE.CylinderGeometry(h * 0.012, h * 0.03, h, 6).translate(x, y0 + h / 2, z));
    capParts.push(snowMound(h * 0.07, h * 0.1, 7, sd + 91.3).translate(x, y0 + h * 0.025, z));
    const n = 8 + Math.floor(rnd(sd) * 5);
    for (let i = 0; i < n; i++) {
      const t = i / (n - 1);
      const y = y0 + h * (0.3 + 0.65 * t);
      const len = h * (0.16 - 0.1 * t) * (0.7 + 0.6 * rnd(sd + i * 3.1));
      const a = rnd(sd + i * 5.7) * Math.PI * 2;
      const g = new THREE.CylinderGeometry(h * 0.004, h * 0.007, len, 4);
      g.translate(0, len / 2, 0);
      g.rotateZ(Math.PI / 2 - 0.12);
      g.rotateY(-a);
      g.translate(x, y, z);
      trunkParts.push(g);
      const sx = x + Math.cos(a) * len * 0.55, sz = z + Math.sin(a) * len * 0.55;
      capParts.push(new THREE.BoxGeometry(len * 0.8, h * 0.006, h * 0.014).rotateY(-a).translate(sx, y + len * 0.11 + h * 0.006, sz));
    }
  };
  /* **집 안에 나무가 서 있었다** (2026-09-10, Chris: 「집 안에 나무 있거든」).
   * 대량 배치 다섯 곳 중 **한 곳만** 건물을 피하고 나머지 넷은 그냥 통과했다.
   * 여기서 한 번 정의해 다섯 곳이 같이 쓴다 — 건물 바깥 5m 와 출입구 앞은 비운다. */
  const treeBlocks = BUILDINGS.map((q) => ({
    x1: Math.min(q.x1, q.x2) - 5, x2: Math.max(q.x1, q.x2) + 5,
    z1: Math.min(q.z1, q.z2) - 5, z2: Math.max(q.z1, q.z2) + 5,
  }));
  const treeDoors = BUILDINGS.flatMap((q) => q.entrances.map((en) => [en.x, en.z] as const));
  const blocked = (x: number, z: number) =>
    treeBlocks.some((a2) => x > a2.x1 && x < a2.x2 && z > a2.z1 && z < a2.z2) ||
    treeDoors.some(([dx, dz]) => Math.hypot(x - dx, z - dz) < 8);

  /**
   * **대량 배치 한 그루.** 방풍림·능선·마을 주변 다섯 곳이 전부
   * `conifer(..., 5, 3)` 이었다 — 층 수 3 고정에 씨앗도 안 넘겨서 층 흔들기가
   * 죽어 있었고, 그래서 그 나무들이 **높이만 다른 같은 나무**였다
   * (2026-09-09 Chris: 「나무들이 너무 구려」).
   * 좌표 해시로 종과 층 수를 같이 고른다 — 같은 자리는 늘 같은 나무라 컷이 재현된다.
   */
  const plant = (x: number, z: number, h: number, sd: number) => {
    if (blocked(x, z)) return;   // 건물·출입구 앞에는 안 심는다
    const r = rnd(sd + 0.5);
    // 강원도 비율: 소나무 35 · 신갈(활엽) 25 · 잣(원뿔) 18 · 낙엽송 12 · 관목 10
    if (r < 0.10) snowBush(x, z, 0, 0.9 + rnd(sd + 3.3) * 0.8, sd);
    else if (r < 0.22) larch(x, z, 0, h * 1.35, sd);
    else if (r < 0.40) conifer(x, z, 0, h, 5, 3 + Math.floor(rnd(sd + 8.1) * 3), sd);
    else if (r < 0.65) bareTree(x, z, 0, h * 0.92, sd);
    else redPine(x, z, 0, h * 1.05, sd);
  };
  /**
   * **겨울 활엽수 — 잎이 없는 나무.**
   *
   * 마을 나무가 전부 잣나무 한 종이라 어느 컷을 찍어도 같은 실루엣이 늘어섰다.
   * 강원 산간 마을 겨울에 실제로 서 있는 것은 침엽수와 **잎 떨어진 참나무·느티나무**가
   * 섞인 모습이다. 활엽수는 원뿔이 아니라 **줄기에서 갈라진 가지**라, 원뿔 하나만
   * 더 놓아서는 얻을 수 없는 실루엣이다 — 그래서 별도로 짠다.
   *
   * 눈은 **가지 윗면에만** 얹힌다. 이 파일이 침엽수에서 이미 배운 규칙이다
   * (「잎은 눈이 아니다」). 가지마다 납작한 원뿔을 얹어 그 선반을 만든다.
   */
  const bareTree = (x: number, z: number, y0: number, h: number, sd: number) => {
    trunkParts.push(
      new THREE.CylinderGeometry(h * 0.018, h * 0.045, h * 0.78, 6).translate(x, y0 + h * 0.39, z),
    );
    // 밑동 눈 — 침엽수와 같은 이유로 검은 토막을 덮는다
    capParts.push(new THREE.ConeGeometry(h * 0.1, h * 0.14, 7).translate(x, y0 + h * 0.03, z));

    const n = 6 + Math.floor(rnd(sd) * 3);
    for (let i = 0; i < n; i++) {
      const t01 = i / (n - 1);
      const yb = y0 + h * (0.42 + 0.34 * t01);
      // 위로 갈수록 가지가 짧고 더 곧게 선다
      const len = h * (0.34 - 0.16 * t01) * (0.8 + 0.4 * rnd(sd + i * 2.3));
      const tilt = (0.95 - 0.35 * t01) * (0.85 + 0.3 * rnd(sd + i * 3.7));
      const a = (i / n) * Math.PI * 2 + rnd(sd + i * 5.1) * 1.1;
      const br = h * 0.011 * (1 - 0.4 * t01);

      /**
       * **가지 하나를 곧은 막대로 두면 옷걸이가 된다.** 첫 판이 그랬다 — 줄기에
       * 말뚝 여섯 개를 박아 놓은 꼴이었다. 실제 겨울나무의 실루엣을 만드는 것은
       * **갈래**다. 그래서 가지마다 한 번 더 갈라 둘로 만든다. 갈래 가지는
       * 어미의 65% 지점에서 나오고, 굵기는 절반, 길이는 45%, 각도는 더 선다.
       * 눈은 두 가지 **각각의 위**에 얹는다.
       */
      const limb = (ox: number, oy: number, oz: number, ln: number, tl: number, az: number, rad: number) => {
        const g = new THREE.CylinderGeometry(rad * 0.45, rad, ln, 5);
        g.translate(0, ln / 2, 0);      // 원점을 가지 밑동으로
        g.rotateZ(tl);
        g.rotateY(az);
        g.translate(ox, oy, oz);
        trunkParts.push(g);
        /**
         * 가지 끝 좌표 — 갈래와 눈 선반이 같이 쓴다.
         * **회전 순서를 그대로 풀어야 한다.** 원통은 +Y 로 서 있고 밑동이 원점이며
         * `rotateZ(tl)` → `rotateY(az)` 순으로 돈다. (0,s,0) 은
         * rotateZ 뒤 (−s·sin tl, s·cos tl, 0), rotateY 뒤
         * (−s·sin tl·cos az, s·cos tl, +s·sin tl·sin az) 다.
         * 처음엔 x 에 sin(az), z 에 cos(az) 를 넣고 부호도 빠뜨렸다 —
         * 그래서 갈래 가지와 눈이 나무에서 **떨어져 하늘에 떠다녔다**
         * (2026-09-06 Chris: 「뭔 나무가지가 날아댕겨」).
         */
        const tip = (f: number): [number, number, number] => {
          const sN = ln * f;
          return [
            ox - Math.sin(tl) * Math.cos(az) * sN,
            oy + Math.cos(tl) * sN,
            oz + Math.sin(tl) * Math.sin(az) * sN,
          ];
        };
        const [sx2, sy2, sz2] = tip(0.55);
        const sr = rad * 3.0;
        capParts.push(new THREE.ConeGeometry(sr, sr * 0.32, 6).translate(sx2, sy2 + rad * 0.7, sz2));
        return tip;
      };

      const tip = limb(x, yb, z, len, tilt, a, br);
      const [fx, fy, fz] = tip(0.65);
      limb(fx, fy, fz, len * 0.45, tilt * 0.55, a + (rnd(sd + i * 8.9) - 0.5) * 1.6, br * 0.55);
    }
    // 우듬지 — 가지 위로 줄기 끝이 조금 더 솟는다
    capParts.push(new THREE.ConeGeometry(h * 0.03, h * 0.05, 6).translate(x, y0 + h * 0.79, z));
  };

  const treeGroup = new THREE.Group(); treeGroup.name = 'trees';
  vg.add(treeGroup);
  const plantTrees = () => {
    const mine = { trunk: [] as THREE.BufferGeometry[], pine: [] as THREE.BufferGeometry[], needle: [] as THREE.BufferGeometry[], cap: [] as THREE.BufferGeometry[] };
    const base = T; T = mine;
    for (const t of OUTDOOR.trees) {
      // 언덕 위 나무는 밑동이 단 위(y=3.6)에 앉는다. y 를 안 더하면 공중에 뜬다
      // 키가 전부 같으면 심어 놓은 티가 난다 — 좌표로 흔든다
      const j = Math.abs(Math.round(t.x * 3 + t.z * 7)) % 5;
      const sd = t.x * 13.7 + t.z * 29.3;
      const hh2 = (t.h ?? 5.4) * (0.86 + j * 0.07);
      /**
       * **수종을 섞는다.** 좌표 해시로 약 1/3 을 활엽수로 돌린다. 레벨 데이터를
       * 건드리지 않아도 되고, 같은 자리는 늘 같은 종이라 컷이 재현된다.
       */
      if (rnd(sd + 0.5) < 0.34) bareTree(t.x, t.z, t.y ?? 0, hh2 * 0.96, sd);
      else conifer(t.x, t.z, t.y ?? 0, hh2, 7, 4 + (j % 3), sd);
    }
    T = base;
    for (const [parts, mat] of [[mine.trunk, trunk], [mine.pine, pineTrunk], [mine.needle, needle], [mine.cap, snowCap]] as const) {
      if (!parts.length) continue;   // 빈 통을 병합하면 `undefined.index` 로 터진다 — 편집 나무엔 소나무가 없다
      const merged = mergeGeometries(parts, false);
      for (const geo of parts) geo.dispose();
      if (merged) treeGroup.add(new THREE.Mesh(merged, mat));
    }
  };
  plantTrees();
  /** 편집 가능한 나무만 다시 심는다 — 재질은 공용이라 버리지 않는다 */
  const rebuildTrees = () => {
    treeGroup.traverse((o2) => { const m = o2 as THREE.Mesh; if (m.geometry) m.geometry.dispose(); });
    treeGroup.clear();
    plantTrees();
  };

  //
  // **화면이 구린 진짜 이유는 기술이 아니라 비어 있어서다.** 700×700 세계에
  // 나무가 열여덟 그루였다. 능선 원뿔과 마을 사이가 통째로 백지라, 마을이
  // 「눈밭에 놓인 모형」으로 읽혔다. vegetation 스킬이 못 박아 둔 것이 이것이다 —
  // 「검은 배경의 나무 한 그루는 유효한 품질 검사가 아니다. 잎 가장자리 대비도,
  // 대기 원근도, 지면 접지도, 크기 단서도 사라진다.」 그 문서의 기준 구도는
  // 배경목 100 그루다.
  //
  // 안개(FogExp2 0.0125)가 60m 에서 43%, 120m 에서 91% 를 먹으므로, 이 나무들은
  // 형태가 아니라 **밀도와 원근**으로만 일한다. 그래서 면을 깎고(seg 5, 층 3)
  // 대신 수를 늘린다.
  //
  // **300 그루를 뿌렸는데 화면이 그대로였다.** 세어 보니 243 그루가 살아는
  // 있었다 — 안 보였을 뿐이다. 이유가 둘이다: (1) 언덕 북쪽 땅은 다시 y=0 이라
  // 3.6m 단 뒤로 **가라앉는다**, (2) 나머지는 100m 밖이라 안개가 91% 를 먹는다.
  // 배경을 채우려면 「멀리 많이」가 아니라 **능선 위로 머리가 나오는 자리**와
  // **중경**이다. 그래서 세 띠로 나눈다.
  {
    const fr = (n: number) => n - Math.floor(n);
    const fh = (i: number, sd: number) => fr(Math.sin(i * 127.1 + sd * 311.7) * 43758.5453);

    // (1) 능선 뒤 — 언덕 마루(y 3.6) **위로** 우듬지가 올라와야 의미가 있다.
    // 아래 컷(z -27)에서 눈높이 1.6m, 마루까지 21m 라 마루선이 5.4도다.
    // 60m 뒤 나무가 그보다 높이 서려면 키가 9m 는 되어야 한다. 그래서 이 띠만
    // 키를 크게 준다 — 원경이라 형태는 어차피 안개에 먹힌다.
    for (let i = 0; i < 80; i++) {
      const x = (fh(i, 11) - 0.5) * 132;
      const z = -84 - fh(i, 12) * 26;
      plant(x, z, 8.6 + fh(i, 13) * 5.4, x * 13.7 + z * 29.3);
    }

    // (2) 중경 — 언덕 좌우로 붙은 땅. 마을과 대로 위에는 심지 않는다.
    for (let i = 0; i < 90; i++) {
      const side = fh(i, 21) < 0.5 ? -1 : 1;
      const x = side * (27 + fh(i, 22) ** 0.7 * 42);
      const z = -78 + fh(i, 23) * 66;
      if (Math.abs(x) < 26) continue;
      if (z > -34 && z < 46 && Math.abs(x) < 34) continue; // 밭·울타리 띠
      plant(x, z, 5.2 + fh(i, 24) * 4.4, x * 17.3 + z * 11.9);
    }

    /**
     * (2.5) **마을 띠 — 이 단계에서 새로 넣은 것.**
     *
     * 세 띠를 짜 놓고 보니 전부 언덕 주변이었다. 대로 컷과 배정호 외길 컷에는
     * 나무가 한 그루도 안 나온다 — 집 여섯 채가 **아무것도 없는 흰 판** 위에
     * 떠 있었고, 그래서 집끼리의 거리도 마을의 크기도 읽히지 않았다.
     *
     * 문제는 마을 안은 비울 곳이 많다는 것이다. 그래서 뿌리고 지운다:
     * 건물 발자국 +5m, 길 +3.5m, **현관 반경 11m**(사람이 드나드는 마당이다),
     * `CLEARINGS`(카메라가 서는 자리), 운동장(정면 컷의 아래 절반)을 뺀다.
     *
     * **현관 반경만으로는 모자랐다.** 명주원 집 컷의 카메라(-44, 4)는 그 집
     * 현관에서 16m 라 이 체에 안 걸렸고, 침엽수 한 그루가 렌즈 앞 0.7m 에
     * 서서 화면을 통째로 덮었다. 그래서 카메라 자리를 따로 적어 둔다.
     */
    {
      const rects = ROADS.map((q) => ({
        x1: Math.min(q.x1, q.x2) - 3.5, x2: Math.max(q.x1, q.x2) + 3.5,
        z1: Math.min(q.z1, q.z2) - 3.5, z2: Math.max(q.z1, q.z2) + 3.5,
      }));
      const bs = BUILDINGS.map((q) => ({
        x1: Math.min(q.x1, q.x2) - 5, x2: Math.max(q.x1, q.x2) + 5,
        z1: Math.min(q.z1, q.z2) - 5, z2: Math.max(q.z1, q.z2) + 5,
      }));
      const doors = BUILDINGS.flatMap((q) => q.entrances.map((en) => [en.x, en.z] as const));
      const inRect = (a: { x1: number; x2: number; z1: number; z2: number }, x: number, z: number) =>
        x > a.x1 && x < a.x2 && z > a.z1 && z < a.z2;
      let placed = 0;
      for (let i = 0; i < 320 && placed < 130; i++) {
        const x = -96 + fh(i, 31) * 214;
        const z = -26 + fh(i, 32) * 102;
        if (Math.abs(x) < 19 && z > 2 && z < 16) continue;          // 운동장 — 정면 컷의 아래 절반
        if (rects.some((a) => inRect(a, x, z))) continue;
        if (bs.some((a) => inRect(a, x, z))) continue;
        if (doors.some(([dx, dz]) => Math.hypot(x - dx, z - dz) < 11)) continue;
        if (CLEARINGS.some((c) => Math.hypot(x - c.x, z - c.z) < c.r)) continue;
        // 집 사이는 작은 나무, 마을 바깥은 큰 나무. 크기가 거리를 만든다
        const far = Math.abs(x) > 66 || z > 48;
        plant(x, z, (far ? 6.0 : 4.2) + fh(i, 33) * (far ? 4.2 : 2.6), x * 7.1 + z * 23.9);
        placed++;
      }
    }

    // (3) 원경 링 — 능선 원뿔과 (1) 사이의 빈틈. 여기는 밀도만 일한다.
    for (let i = 0; i < 150; i++) {
      const ang = fh(i, 1) * Math.PI * 2;
      const rad = 96 + fh(i, 2) ** 0.6 * 96;
      const x = Math.cos(ang) * rad, z = Math.sin(ang) * rad * 0.9 - 20;
      if (Math.abs(x) < 96 && z > -84 && z < 64) continue;
      plant(x, z, 5.0 + fh(i, 3) * 5.6, x * 31.1 + z * 5.7);
    }
  }

  // 마을 가로등. 밤에 밖이 새까매서 아무것도 못 찾으면 게임이 안 된다.
  // 방향광을 올리면 벽을 통과해 실내까지 밝아지므로, 위치가 있는 등으로 푼다.
  const pools = new LightPools();
  const poleMat = new THREE.MeshStandardMaterial({ color: '#2f3338', roughness: 0.7, metalness: 0.4 });
  const headMat = new THREE.MeshBasicMaterial({ color: '#ffd9a0' });
  for (const { x, z, y = 0, dead = false } of OUTDOOR.lamps) {
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.1, 4.4, 8), poleMat);
    pole.position.set(x, y + 2.2, z);
    vg.add(pole);
    const arm = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.08, 0.08), poleMat);
    arm.position.set(x + 0.25, y + 4.3, z);
    vg.add(arm);
    const head = new THREE.Mesh(
      new THREE.BoxGeometry(0.36, 0.16, 0.28),
      dead ? new THREE.MeshStandardMaterial({ color: '#4b4a46', roughness: 0.8 }) : headMat,
    );
    head.position.set(x + 0.5, y + 4.24, z);
    vg.add(head);
    // 나간 가로등 하나는 단서다 (명주원이 마주친 자리가 어두운 이유)
    if (dead) continue;
    const lamp = markDecor(new THREE.PointLight('#ffd2a0', 46, 26, 1.5));   // 가까운 몇 개만 켜진다(lightPool.ts)
    lamp.position.set(x + 0.5, y + 4.1, z);
    vg.add(lamp);

    // **PointLight 하나로는 등이 켜진 것처럼 안 보인다.** 눈밭은 거의 평평해서
    // 감쇠만으로는 밝기 차가 안 읽히고, 공기 중에는 빛이 걸릴 것이 없다.
    // 그래서 바닥에 웅덩이를 깔고 등에서 기둥을 내린다 — 둘 다 데칼이라 공짜다.
    pools.pool(x + 0.5, y + 0.012, z, 5.6, '#ffcf9a', 0.5);
    // **후광이지 고깔이 아니다.** 4.05m 높이 · 밑반지름 2.9m 는 등 하나가 5.8m 짜리
    // 삼각형을 세운다는 뜻이었다. 눈밭에서 등 빛은 등 머리 주위의 후광이다.
    pools.cone(x + 0.5, y + 4.05, z, 2.6, 0.3, 1.7, '#ffc98e', 0.1);
  }
  // 등 전부를 한 덩어리로 굽는다 — 웅덩이 하나, 기둥 하나, 도합 draw call 2
  pools.flush(vg);

  /* --- 학교 운동장 --- */
  // 학교가 학교로 읽히는 두 번째 조건. 조회대와 국기게양대와 골대가 있으면
  // 그 앞의 눈밭은 '빈터'가 아니라 '운동장'이 된다.
  if (OUTDOOR.yard) {
    const y = OUTDOOR.yard;
    const conc = new THREE.MeshStandardMaterial({ color: '#8a8a84', roughness: 0.92 });
    const steel = new THREE.MeshStandardMaterial({ color: '#9aa2ab', roughness: 0.5, metalness: 0.5 });
    // 조회대
    vg.add(place(new THREE.BoxGeometry(3.6, 0.5, 2.0), conc, y.podium[0], 0.25, y.podium[1]));
    vg.add(place(new THREE.BoxGeometry(3.9, 0.12, 2.3), snowMat2, y.podium[0], 0.56, y.podium[1]));
    // 국기게양대
    vg.add(place(new THREE.CylinderGeometry(0.07, 0.09, 8.4, 8), steel, y.flagpole[0], 4.2, y.flagpole[1]));
    // 축구 골대 — 운동장의 축을 그린다
    for (const [gx, gz, ax] of y.goals) {
      for (const s of [-1, 1]) {
        const px = ax === 'x' ? gx + s * 3.2 : gx;
        const pz = ax === 'x' ? gz : gz + s * 3.2;
        vg.add(place(new THREE.BoxGeometry(0.1, 2.3, 0.1), steel, px, 1.15, pz));
      }
      const bar = ax === 'x' ? new THREE.BoxGeometry(6.5, 0.1, 0.1) : new THREE.BoxGeometry(0.1, 0.1, 6.5);
      vg.add(place(bar, steel, gx, 2.3, gz));
    }
    // 교문 문주 — 길이 여기서 끊기고 운동장이 시작된다
    for (const [px, pz] of y.gate ?? []) {
      vg.add(place(new THREE.BoxGeometry(0.55, 2.8, 0.55), conc, px, 1.4, pz));
      vg.add(place(new THREE.BoxGeometry(0.7, 0.14, 0.7), snowMat2, px, 2.87, pz));
    }
  }

  /* --- 마을의 뒤 --- */
  // 마을 뒤가 무한한 눈밭이면 세트로 보인다. 경계를 만든다:
  // 뒷산 비탈 · 방풍림 한 줄 · 밭두렁 돌담.
  {
    const slope = new THREE.MeshStandardMaterial({ color: '#323c4c', roughness: 1 });
    // 뒷산 — 마을 바로 뒤에서 올라간다. 먼 능선과 달리 이건 가깝고 넓다
    for (const [x, z, w2, h2, d2] of OUTDOOR.hills) {
      const m = new THREE.Mesh(new THREE.ConeGeometry(w2 * 0.42, h2, 16), slope);
      m.position.set(x, h2 / 2 - 5, z);
      m.scale.z = d2 / (w2 * 0.84);
      vg.add(m);
    }
    // 담장 — 돌담이 아니라 **시멘트블록 담**이다. 새마을운동 환경개선사업의
    // 「담장 바로잡기」로 마을 담이 죄 블록으로 바뀌었다
    const stone = new THREE.MeshStandardMaterial({ color: '#918c80', roughness: 0.95 });
    for (const { x1, z1, x2, z2 } of OUTDOOR.fences) {
      const len = Math.hypot(x2 - x1, z2 - z1);
      const wall = new THREE.Mesh(new THREE.BoxGeometry(len, 0.85, 0.55), stone);
      wall.position.set((x1 + x2) / 2, 0.42, (z1 + z2) / 2);
      vg.add(wall);
      const cap = new THREE.Mesh(new THREE.BoxGeometry(len, 0.14, 0.72), snowMat2);
      cap.position.set((x1 + x2) / 2, 0.9, (z1 + z2) / 2);
      vg.add(cap);
    }
    /**
     * **마당의 야적물.**
     *
     * 별동은 덩어리를 둘로 쪼개고 지붕에 함석 골까지 넣었는데도 「개 구리다」
     * 였다. 원인은 건물이 아니라 그 앞의 **백지 눈밭**이었다 — 32m 짜리
     * 가로선 앞에 아무것도 없으니 크기도 용도도 안 읽혔다. 사람 키 안팎의
     * 세로 덩어리 몇 개면 그 둘이 동시에 생긴다.
     *
     * 위에 눈을 얹는 것이 핵심이다. 안 얹으면 「방금 갖다 놓은 것」이라
     * 눈 온 지 사흘 된 마을과 어긋난다.
     */
    const bark = new THREE.MeshStandardMaterial({ color: '#4b3b2b', roughness: 0.95 });
    const crateM = new THREE.MeshStandardMaterial({ color: '#6b573c', roughness: 0.92 });
    // LP 가스통. 시골 작업장의 붉은 통 두 개는 그 자체로 「여기 불을 쓴다」다
    const cylM = new THREE.MeshStandardMaterial({ color: '#8f4f40', roughness: 0.5, metalness: 0.12 });
    const sackM = new THREE.MeshStandardMaterial({ color: '#7c7a71', roughness: 0.96 });
    const logGeo = new THREE.CylinderGeometry(0.085, 0.085, 2.2, 7);
    logGeo.rotateZ(Math.PI / 2);   // 축을 눕혀 x 방향으로
    for (const st of OUTDOOR.stacks ?? []) {
      const grp = new THREE.Group();
      grp.position.set(st.x, 0, st.z);
      grp.rotation.y = st.rot ?? 0;
      const put = (geo: THREE.BufferGeometry, mat: THREE.Material, x: number, y: number, z: number) => {
        const m = new THREE.Mesh(geo, mat);
        m.position.set(x, y, z);
        m.castShadow = true;
        grp.add(m);
      };
      if (st.kind === 'firewood') {
        // 통나무를 벽처럼 쌓는다. 열마다 반 칸씩 밀어야 격자가 안 된다
        for (let r = 0; r < 5; r++) {
          const cols = r === 4 ? 2 : 3;
          for (let c = 0; c < cols; c++) {
            put(logGeo, bark, 0, 0.09 + r * 0.17, (c - (cols - 1) / 2) * 0.19 + (r % 2) * 0.09);
          }
        }
        put(new THREE.BoxGeometry(2.24, 0.09, 0.66), snowMat2, 0, 0.9, 0.04);
      } else if (st.kind === 'crates') {
        const c = new THREE.BoxGeometry(0.78, 0.42, 0.6);
        put(c, crateM, 0, 0.21, 0);
        put(c, crateM, 0.06, 0.63, -0.05);
        put(c, crateM, -0.1, 1.05, 0.03);
        put(new THREE.BoxGeometry(0.82, 0.07, 0.64), snowMat2, -0.1, 1.29, 0.03);
      } else if (st.kind === 'cylinders') {
        const cyl = new THREE.CylinderGeometry(0.16, 0.16, 0.74, 12);
        const collar = new THREE.CylinderGeometry(0.07, 0.07, 0.12, 8);
        for (const dx of [-0.19, 0.19]) {
          put(cyl, cylM, dx, 0.37, 0);
          put(collar, cylM, dx, 0.8, 0);
        }
      } else {
        // 흙 포대 — 팔레트 위에 눌린 자루 넷
        put(new THREE.BoxGeometry(1.1, 0.11, 0.8), bark, 0, 0.055, 0);
        for (let i = 0; i < 4; i++) {
          put(new THREE.BoxGeometry(0.5, 0.17, 0.34), sackM,
            (i % 2 ? 0.26 : -0.26), 0.2 + Math.floor(i / 2) * 0.18, (i % 2 ? 0.1 : -0.1));
        }
        put(new THREE.BoxGeometry(1.14, 0.07, 0.84), snowMat2, 0, 0.52, 0);
      }
      vg.add(grp);
    }

    // 방풍림 — 담 뒤로 나무를 줄지어 심는다
    for (const gv of OUTDOOR.groves) {
      for (let i = 0; i < gv.n; i++) {
        const t = gv.n === 1 ? 0 : i / (gv.n - 1);
        const tx = gv.x1 + (gv.x2 - gv.x1) * t + ((i * 7) % 3) * 0.4;
        const tz = gv.z1 + (gv.z2 - gv.z1) * t + ((i * 5) % 3) * 0.5;
        plant(tx, tz, 4.6 + ((i * 11) % 5) * 0.3, tx * 19.7 + tz * 3.3);
      }
    }
  }

  /* **40m 타일로 나눠 합친다** (2026-09-15, Chris: 「프레임 드랍이 없어야 함」). 방풍림 수백 그루를 재질당
   * 메시 하나로 합쳤더니 눈 얹힌 가지만 39만 삼각형이 되었고, 맵 전체 크기라 실내에서 벽을 보고 있어도
   * 절두체 컬링이 못 걸렀다 — 전체 185만 중 3분의 1이 늘 바깥 나무였다. 타일이면 보이는 쪽만 그린다. */
  const TILE = 40;
  for (const [parts, mat] of [[trunkParts, trunk], [pineTrunkParts, pineTrunk], [needleParts, needle], [capParts, snowCap]] as const) {
    const tiles = new Map<string, THREE.BufferGeometry[]>();
    const c = new THREE.Vector3();
    for (const geo of parts) {
      geo.computeBoundingBox(); geo.boundingBox!.getCenter(c);
      const key = `${Math.floor(c.x / TILE)},${Math.floor(c.z / TILE)}`;
      const list = tiles.get(key); if (list) list.push(geo); else tiles.set(key, [geo]);
    }
    for (const list of tiles.values()) {
      const merged = list.length === 1 ? list[0] : mergeGeometries(list, false);
      if (list.length > 1) for (const geo of list) geo.dispose();
      if (merged) { const m = new THREE.Mesh(merged, mat); m.matrixAutoUpdate = false; vg.add(m); }
    }
  }

  const snow = buildSnow(time);
  vg.add(snow.mesh);

  // 지붕 밑을 파낸다. 처마 내밈만큼 넉넉히 잡고 마루 높이까지.
  // **이야기상 눈이 들어와야 하는 집은 여기서 빼면 된다** (`snowIn`).
  if (village) {
    const rb = snow.uniforms.uRoofBox.value as Float32Array;
    const rt = snow.uniforms.uRoofTop.value as Float32Array;
    let n = 0;
    for (const b of BUILDINGS) {
      if (n >= 12 || b.snowIn) continue;
      const x1 = Math.min(b.x1, b.x2) - 0.6, x2 = Math.max(b.x1, b.x2) + 0.6;
      const z1 = Math.min(b.z1, b.z2) - 0.6, z2 = Math.max(b.z1, b.z2) + 0.6;
      const span = Math.min(x2 - x1, z2 - z1);
      const pitch = ((b.style?.pitchDeg ?? 26) * Math.PI) / 180;
      rb.set([x1, z1, x2, z2], n * 4);
      rt[n] = (b.baseY ?? 0) + WALL_H + 0.34 + (span / 2) * Math.tan(pitch);
      n++;
    }
    snow.uniforms.uRoofN.value = n;
  }

  // 발자국. 눈이 없는 사건(초치성)에는 만들지 않는다
  const tracks = village ? new SnowTracks(vg) : null;
  /**
   * **마루와 댓돌에는 자국이 안 남는다.**
   *
   * 방 안인지는 부르는 쪽이 `roomAt` 으로 거른다. 여기서 거르는 건 그걸로 안
   * 걸리는 것 — 툇마루·댓돌·기단이다. 셋 다 건물 바로 앞 1.5m 안에 있고
   * **바닥보다 높다**(마루 0.45, 댓돌 0.09). 그러니 높이로 가른다:
   * 건물 언저리인데 그 집 바닥보다 20cm 넘게 높으면 그건 눈이 아니라 나무다.
   *
   * 건물 언저리를 통째로 빼면 안 된다 — 그러면 현관까지 이어진 발자국이라는,
   * 이 기능에서 제일 보고 싶은 그림이 사라진다.
   */
  const onSnow = (x: number, y: number, z: number) => {
    for (const b of BUILDINGS) {
      if (x < Math.min(b.x1, b.x2) - 1.5 || x > Math.max(b.x1, b.x2) + 1.5) continue;
      if (z < Math.min(b.z1, b.z2) - 1.5 || z > Math.max(b.z1, b.z2) + 1.5) continue;
      if (y > (b.baseY ?? 0) + 0.2) return false;
    }
    return true;
  };

  return {
    update: (t, cameraPos) => {
      time.value = t;
      snow.uniforms.uCameraPos.value.copy(cameraPos);
      // 하늘 돔은 카메라를 따라온다. 안 그러면 마을 끝에서 돔 밖으로 나간다
      sky.position.copy(cameraPos);
      tracks?.update(t);
    },
    setSnow: (f) => {
      snow.geo.instanceCount = Math.max(1, Math.floor(THREE.MathUtils.clamp(f, 0, 1) * snow.COUNT));
    },
    footstep: (id, x, y, z, yaw, indoor) => {
      tracks?.follow(id, x, y, z, yaw, !indoor && onSnow(x, y, z));
    },
    rebuildTrees,
  };
}
